import {
  capabilityToolDefinition,
  getCapability,
  operationalGuideForCapability,
  parseCapabilityInput,
  parseCapabilityOutput,
  searchCapabilities,
  type CapabilityDefinition,
  type RestCapabilityHandler,
  installationGuidance,
  ACTIVITY_LABELS,
  DATUM_TOLERANCE_MM,
  heightCandidates,
  inferOpeningKind,
  operationEnvelopeAssessment,
  reachAssessment,
  verticalPlacementBand,
  type EnvironmentReading,
  type HeightCandidate,
  type TaskExecutionArtifact,
  type TaskPlan,
  type TypologyDescriptor,
  type TypologyGuidance,
} from "@calcplan/ai-capabilities";
import type {
  CatalogItem,
  CameraState,
  Measurement,
  Opening,
  SceneDocument,
  SceneElement,
  SceneLayer,
  SceneOperation,
  Vec2,
  Vec3,
  Wall,
} from "@calcplan/domain";
import type { DynamicComponentGraph, DynamicNode } from "@calcplan/dynamic-components";
import { api, apiAssetUrl } from "../lib/api";
import { catalogItemForAgent } from "./ai-catalog-result";
import { resolveArchitecturalFloorPlacement } from "./ai-architectural-placement";
import {
  findReusableCreation,
  markCreationsDeleted,
  normalizeCreationInstanceKey,
  refreshCreationLedger,
  registerCreation,
} from "./ai-execution-ledger";
import { subjectNamesAgree, elementRequiresAgentDesignReview, environmentIssueDisposition, fixtureInterferenceObservations, independentAgentDesignReviewAspects, independentInstallationIssues, independentUseIssues, professionalEnvironmentEvidence, professionalEnvironmentJudgmentIssues, requiredAgentDesignReviewAspects, structuredFunctionReview, type EnvironmentReviewIssue, type InstallationReviewDescriptors, type ReviewTargetProfile } from "./ai-professional-review";
import { checkElementClearance, checkElementGrounded, checkElementInstallation } from "./ai-scene-checks";
import { buildEnvironmentReading, observedTypologyDescriptor } from "./ai-environment-reading";
import {
  captureEditorViewport,
  type InspectionCameraMetadata,
  type ViewportCaptureResult,
} from "./ai-viewport-capture";
import { planInteraction, type InteractionDesiredState, type InteractionState } from "./component-interaction";
import { elementComponentry } from "./ai-element-componentry";
import {
  dynamicAssetIdFor,
  evaluateDynamicFor,
  loadDynamicGraph,
  overrideKey,
  readAttributeOverrides,
  readLoadedDynamicGraph,
  registerDynamicCatalog,
} from "./dynamic-parts";
import {
  readElementPlacementSurface,
  resolveSurfacePlacement,
  resolveWallMountSurface,
  withElementPlacementSurface,
  type PlacementSurfaceConstraint,
  type WallMountFace,
} from "./placement";
import { agentCallNeedsConfirmation } from "./ai-confirmation-policy";
import { uncoveredVisualVerdictDisposition } from "./ai-visual-history";
import {
  renderedElementCenterMm,
  renderedElementEnvelope,
  renderedElementEnvelopeState,
  renderedElementSizeMm,
  renderedElementVerticalIntervalMm,
  waitForRenderedElementEnvelope,
} from "./rendered-envelope";
import { useEditorStore, withAgentTurn } from "./store";

const MAX_RESULT_BYTES = 256 * 1024;

interface ReviewLedgerEntry {
  round: number;
  captureId: string;
  elementId: string;
  elementName: string;
  verdict: "approved" | "rejected" | "unverifiable";
  failedAspects: string[];
  corrections: string[];
  summary: string;
  stateThen: { bottomMm: number; topMm: number } | null;
}

/** Aspects a declared, validated vertical datum decides; the critic may observe, not re-decide. */
const DATUM_PROTECTED_ASPECTS = new Set(["use_height", "datum_alignment", "spacing", "installation", "scale", "proportion", "alignment"]);

type RecordInput = Record<string, unknown>;

interface AgentExecutionRecord {
  sequence: number;
  recordedAt: string;
  capability: string;
  arguments: unknown;
  result: unknown;
  durationMs: number;
  failed: boolean;
}

interface ViewportCaptureEvidence {
  captureId: string;
  epoch: number;
  targetIds: string[];
  coveredIds: string[];
  context: "targets" | "environment";
  overviewComplete: boolean;
  views: InspectionCameraMetadata[];
}

export function coverageEvidenceForCapture<T extends { epoch: number; context: "targets" | "environment" }>(
  evidence: Iterable<T>,
  currentEpoch: number,
  requestedContext: "targets" | "environment",
): T[] {
  // Coverage is about having two legible, independent views of each pending
  // target in the current visual epoch; the context of the capture that
  // produced a view does not change its value. Keeping contexts apart made a
  // directed recapture unable to finish what the environment packet had
  // started, so the final review looped on captures that never "completed".
  void requestedContext;
  return [...evidence].filter((candidate) => candidate.epoch === currentEpoch);
}

export function shouldUseAdaptiveRecovery(input: {
  context: "targets" | "environment";
  view: string;
  hasExplicitDirection: boolean;
  environmentOverviewComplete: boolean;
}): boolean {
  if (input.view !== "adaptive" || input.hasExplicitDirection) return false;
  // A final environment review must first establish its fresh panorama,
  // plan and facade. Focused target evidence from the same epoch may satisfy
  // detail coverage, but it must never turn that first global packet into a
  // custom recovery camera.
  return input.context === "targets" || input.environmentOverviewComplete;
}

interface StepMutationEvidence {
  stepId: string;
  capability: string;
  elementIds: string[];
}

export class EditorCapabilityRuntime {
  private readonly catalogById = new Map<string, CatalogItem>();
  private readonly viewportCapturesByTurn = new Map<string, number>();
  private readonly pendingVisualIdsByTurn = new Map<string, Set<string>>();
  private readonly environmentReadingByTurn = new Map<string, EnvironmentReading>();
  /** The critic's own verdicts in this turn, per element — the memory a blind review needs to stay consistent with itself. */
  private readonly reviewLedgerByTurn = new Map<string, ReviewLedgerEntry[]>();
  private readonly mutatedElementIdsByTurn = new Map<string, Set<string>>();
  private readonly mutatedElementIdsByStepByTurn = new Map<string, Map<string, Set<string>>>();
  private readonly stepMutationEvidenceByTurn = new Map<string, StepMutationEvidence[]>();
  private readonly viewportEvidenceByTurn = new Map<string, Map<string, ViewportCaptureEvidence>>();
  private readonly viewportCaptureAliasByTurn = new Map<string, Map<string, string>>();
  private readonly visualEpochByTurn = new Map<string, number>();
  private readonly approvedVisualIdsByTurn = new Map<string, Set<string>>();
  private readonly environmentApprovedTurns = new Set<string>();
  private readonly taskPlanByTurn = new Map<string, TaskPlan>();
  private readonly activeExecutionStepByTurn = new Map<string, string>();
  private readonly executionArtifactsByTurn = new Map<string, TaskExecutionArtifact[]>();
  private readonly correctionAllowedIdsByTurn = new Map<string, Set<string>>();
  private readonly auditByTurn = new Map<string, AgentExecutionRecord[]>();
  private readonly auditSequenceByTurn = new Map<string, number>();
  private progressHandler: ((message: string) => void) | null = null;

  constructor(catalog: readonly CatalogItem[]) {
    this.updateCatalog(catalog);
  }

  updateCatalog(catalog: readonly CatalogItem[]): void {
    registerDynamicCatalog(catalog);
    for (const item of catalog) {
      this.catalogById.set(item.id, item);
      this.catalogById.set(item.stableItemId, item);
    }
  }

  setProgressHandler(handler: ((message: string) => void) | null): void {
    this.progressHandler = handler;
  }

  setTaskPlan(turnId: string, plan: TaskPlan): void {
    this.taskPlanByTurn.set(turnId, plan);
    this.correctionAllowedIdsByTurn.delete(turnId);
    this.environmentApprovedTurns.delete(turnId);
  }

  syncTaskPlan(turnId: string, plan: TaskPlan): void {
    this.taskPlanByTurn.set(turnId, plan);
  }

  setActiveExecutionStep(turnId: string, stepId: string | null): void {
    if (stepId) this.activeExecutionStepByTurn.set(turnId, stepId);
    else this.activeExecutionStepByTurn.delete(turnId);
  }

  executionLedger(turnId: string): TaskExecutionArtifact[] {
    const refreshed = refreshCreationLedger(
      this.executionArtifactsByTurn.get(turnId) ?? [],
      useEditorStore.getState().scene.elements,
    );
    this.executionArtifactsByTurn.set(turnId, refreshed);
    return refreshed.map((artifact) => ({ ...artifact }));
  }

  independentReviewSubjects(turnId: string, onlyElementIds?: ReadonlySet<string>, stepId?: string | null): Array<{
    elementId: string;
    name: string;
    agentOwnedAttributes: TaskPlan["designResolutions"][number]["attribute"][];
    requiredAspects: ReturnType<typeof requiredAgentDesignReviewAspects>;
  }> {
    const scene = useEditorStore.getState().scene;
    return [...(this.mutatedElementIdsByTurn.get(turnId) ?? [])]
      .filter((elementId) => !onlyElementIds || onlyElementIds.has(elementId))
      .flatMap((elementId) => {
        const element = scene.elements.find((candidate) => candidate.id === elementId);
        if (!element) return [];
        const stepScope = stepId ? this.stepDesignScope(turnId, elementId, element.name, stepId) : null;
        const resolutions = stepScope?.applies
          ? stepScope.resolutions
          : this.designResolutionsForElement(turnId, elementId, element.name);
        const agentOwnedAttributes = [...new Set(resolutions
          .filter((resolution) => resolution.authority === "agent")
          .map((resolution) => resolution.attribute))];
        const requiredAspects = stepScope?.applies
          ? stepScope.aspects
          : this.requiredDesignAspectsForElement(turnId, elementId, element.name);
        return [{ elementId, name: element.name, agentOwnedAttributes, requiredAspects }];
      });
  }

  /**
   * A local step review judges the step's own deliverable. When the element
   * has design resolutions in that step, only those define what the critic
   * must assess; re-demanding the full rubric of an earlier step (proportion,
   * installation, rhythm…) during a finishing step produced "faltou avaliar"
   * rejections in a loop, even though those aspects were already approved.
   */
  stepDesignScope(turnId: string, elementId: string, elementName: string, stepId: string): {
    applies: boolean;
    resolutions: TaskPlan["designResolutions"];
    aspects: ReturnType<typeof requiredAgentDesignReviewAspects>;
    agentAuthored: boolean;
  } {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan || plan.designFreedom === "strict") return { applies: false, resolutions: [], aspects: [], agentAuthored: false };
    const stepResolutions = (plan.designResolutions ?? []).filter((resolution) => (
      resolution.stepId === stepId && planSubjectsMatch(resolution.subject, elementName)
    ));
    if (!stepResolutions.length) return { applies: false, resolutions: [], aspects: [], agentAuthored: false };
    const agent = stepResolutions.filter((resolution) => resolution.authority === "agent");
    const bound = agent.flatMap((resolution) => resolution.reviewCriteria.map((criterion) => criterion.aspect));
    const generic = independentAgentDesignReviewAspects([...new Set(agent.map((resolution) => resolution.attribute))], this.reviewTargetProfile(elementId));
    return {
      applies: true,
      resolutions: stepResolutions,
      aspects: [...new Set([...generic, ...bound])],
      agentAuthored: agent.length > 0,
    };
  }

  stepHasExecutionEvidence(turnId: string, stepId: string): boolean {
    return this.stepExecutionEvidence(turnId, stepId).length > 0;
  }

  requiredAtomicPlacementCapability(
    turnId: string,
    stepId: string,
  ): "scene.element.place" | "scene.module.install" | null {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan) return null;
    const resolutions = plan.designResolutions.filter((resolution) => resolution.stepId === stepId);
    const hasRelationalResolution = resolutions.some((resolution) => (
      ["position", "orientation", "relation"].includes(resolution.attribute)
      && plan.spatialDecisions.some((decision) => planSubjectsMatch(decision.subject, resolution.subject))
    ));
    if (!hasRelationalResolution) return null;
    const evidence = this.stepMutationEvidenceByTurn.get(turnId) ?? [];
    const alreadyApplied = evidence.some((mutation) => (
      mutation.stepId === stepId
      && ["scene.element.place", "scene.element.mount", "scene.module.install"].includes(mutation.capability)
    ));
    if (alreadyApplied) return null;
    return resolutions.some((resolution) => resolution.attribute === "product")
      ? "scene.module.install"
      : "scene.element.place";
  }

  stepExecutionEvidence(turnId: string, stepId: string): string[] {
    const scene = useEditorStore.getState().scene;
    const mutationEvidence = (this.stepMutationEvidenceByTurn.get(turnId) ?? [])
      .filter((evidence) => evidence.stepId === stepId && evidence.elementIds.length > 0)
      .map((evidence) => {
        const targets = evidence.elementIds
          .map((id) => findSceneTargetName(scene, id) ?? id)
          .join(", ");
        return `${evidence.capability} confirmou alteração em ${targets}.`;
      });
    const creationEvidence = this.executionLedger(turnId)
      .filter((artifact) => artifact.stepId === stepId && artifact.status === "live")
      .map((artifact) => `Instância ${artifact.name} (${artifact.elementId}) permanece ativa na cena.`);
    return [...new Set([...mutationEvidence, ...creationEvidence])].slice(0, 10);
  }

  verifyStepDesignResolutions(turnId: string, stepId: string): string[] {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan || plan.designFreedom === "strict") return [];
    const resolutions = (plan.designResolutions ?? []).filter((resolution) => resolution.stepId === stepId);
    if (!resolutions.length) return [];

    const scene = useEditorStore.getState().scene;
    const allArtifacts = this.executionLedger(turnId).filter((artifact) => artifact.status === "live");
    const artifacts = allArtifacts.filter((artifact) => artifact.stepId === stepId);
    const stepMutationIds = this.mutatedElementIdsByStepByTurn.get(turnId)?.get(stepId) ?? new Set<string>();
    const stepMutations = (this.stepMutationEvidenceByTurn.get(turnId) ?? [])
      .filter((mutation) => mutation.stepId === stepId);
    const expectedCatalogIds = new Set(resolutions
      .filter((resolution) => resolution.attribute === "product" && resolution.targetCatalogItemId)
      .map((resolution) => this.catalogById.get(resolution.targetCatalogItemId!)?.stableItemId ?? resolution.targetCatalogItemId!));
    // Providers can batch independent physical actions before emitting their
    // plan progress calls. Recover a uniquely matching creation from the turn
    // ledger instead of losing it merely because the active UI step had not
    // advanced yet.
    const recoveredArtifacts = artifacts.length || !expectedCatalogIds.size
      ? []
      : allArtifacts.filter((artifact) => expectedCatalogIds.has(artifact.catalogItemId));
    const stepArtifacts = artifacts.length ? artifacts : recoveredArtifacts;
    const stepElementIds = new Set([...stepArtifacts.map((artifact) => artifact.elementId), ...stepMutationIds]);
    const turnMutatedIds = this.mutatedElementIdsByTurn.get(turnId) ?? new Set<string>();
    // A plan legitimately splits "install the module" and "finish the module"
    // into two steps. The element then belongs to the first step's ledger,
    // and a finishing action that is already satisfied (the install copied the
    // reference appearance) is a no-op that never registers as a mutation of
    // the second step. Scoping the check to the second step alone made it
    // impossible to satisfy — the turn of 19/08 07:28 looped 70 model calls on
    // it. When the step owns nothing, verify the decision against the
    // element(s) the resolution's subject actually refers to.
    const subjectElements = (resolution: TaskPlan["designResolutions"][number]): SceneElement[] => {
      const ids = new Set<string>();
      // O vínculo por id declarado no plano vem antes de qualquer casamento de
      // nome: "Móvel baixo claro existente" × "Movel 1 - Completo claro" não
      // compartilham termo de conteúdo e a entrega perdia o crédito (20/08
      // 16:15 — pendência falsa e verificador lateral mudo).
      if (resolution.subjectElementId) ids.add(resolution.subjectElementId);
      const productIds = new Set((plan.designResolutions ?? [])
        .filter((candidate) => candidate.attribute === "product" && candidate.targetCatalogItemId && planSubjectsMatch(candidate.subject, resolution.subject))
        .map((candidate) => this.catalogById.get(candidate.targetCatalogItemId!)?.stableItemId ?? candidate.targetCatalogItemId!));
      for (const artifact of allArtifacts) {
        if (resolution.instanceKey && artifact.instanceKey !== resolution.instanceKey) continue;
        if (productIds.has(artifact.catalogItemId) || planSubjectsMatch(artifact.name, resolution.subject)) ids.add(artifact.elementId);
      }
      for (const id of turnMutatedIds) {
        const element = scene.elements.find((candidate) => candidate.id === id);
        if (element && planSubjectsMatch(element.name, resolution.subject)) ids.add(id);
      }
      return [...ids]
        .map((id) => scene.elements.find((element) => element.id === id))
        .filter((element): element is SceneElement => Boolean(element));
    };
    const issues: string[] = [];
    for (const resolution of resolutions) {
      if (["position", "orientation", "relation"].includes(resolution.attribute)
        && plan.spatialDecisions.some((decision) => planSubjectsMatch(decision.subject, resolution.subject))
        && !stepMutations.some((mutation) => (
          ["scene.element.place", "scene.element.mount", "scene.module.install"].includes(mutation.capability)
          && mutation.elementIds.some((id) => {
            if (resolution.subjectElementId && id === resolution.subjectElementId) return true;
            const mutated = scene.elements.find((candidate) => candidate.id === id);
            return mutated ? planSubjectsMatch(mutated.name, resolution.subject) : false;
          })
        ))) {
        issues.push(`A relação planejada para “${resolution.subject}” ainda não foi aplicada atomicamente neste próprio elemento. Use a capacidade relacional da entrega (scene.element.place/mount ou scene.module.install) no “${resolution.subject}” para calcular posição e orientação juntas antes de revisar ou concluir.`);
      }
      const scopedArtifacts = resolution.instanceKey
        ? stepArtifacts.filter((artifact) => artifact.instanceKey === resolution.instanceKey)
        : stepArtifacts;
      const scopedIds = resolution.instanceKey
        ? new Set(scopedArtifacts.map((artifact) => artifact.elementId))
        : stepElementIds;
      const ownedElements = [...scopedIds]
        .map((id) => scene.elements.find((element) => element.id === id))
        .filter((element): element is SceneElement => Boolean(element));
      // Creating a product is always the step's own work; refinements of an
      // existing subject (appearance, material, dimensions, implantation) may
      // be satisfied by the element wherever it was created.
      const scopedElements = ownedElements.length || resolution.attribute === "product"
        ? ownedElements
        : subjectElements(resolution);
      if (resolution.targetCatalogItemId) {
        const target = this.catalogById.get(resolution.targetCatalogItemId)?.stableItemId ?? resolution.targetCatalogItemId;
        if (!scopedElements.some((element) => element.catalogRef?.itemId === target)
          && !scopedArtifacts.some((artifact) => artifact.catalogItemId === target)) {
          issues.push(`O produto decidido para “${resolution.subject}” ainda não foi implantado nesta entrega.`);
        }
      }
      if (resolution.targetDimensionsMm) {
        if (!scopedElements.length) {
          issues.push(`O elemento de “${resolution.subject}” ainda não existe para receber as dimensões decididas.`);
          continue;
        }
        const target = resolution.targetDimensionsMm;
        const matched = scopedElements.some((element) => {
          // targetDimensionsMm is the nominal/parametric contract chosen by the
          // planner. A dynamic asset may render a smaller occupied volume (for
          // example because its local origin includes an installation recess),
          // which is validated separately by clearance/installation checks.
          const actual: Vec3 = [
            element.dimensionsMm.width * Math.abs(element.transform.scale[0]),
            element.dimensionsMm.height * Math.abs(element.transform.scale[1]),
            element.dimensionsMm.depth * Math.abs(element.transform.scale[2]),
          ];
          return (["width", "height", "depth"] as const).every((dimension, index) => {
            const targetValue = target[dimension];
            return targetValue === undefined || Math.abs((actual[index] ?? 0) - targetValue) <= 2;
          });
        });
        if (!matched) {
          const values = (["width", "height", "depth"] as const)
            .filter((dimension) => target[dimension] !== undefined)
            .map((dimension) => `${dimension} ${Math.round(target[dimension]!)} mm`)
            .join(", ");
          issues.push(`As dimensões nominais/paramétricas decididas para “${resolution.subject}” ainda não foram aplicadas (${values}).`);
        }
      }
      if (resolution.targetMaterialCatalogItemId) {
        const target = this.catalogById.get(resolution.targetMaterialCatalogItemId)?.stableItemId
          ?? resolution.targetMaterialCatalogItemId;
        const matched = scopedElements.some((element) => (
          element.materialId === target
          || Object.values(element.materialAssignments).some((assignment) => assignment.materialId === target)
        ));
        if (!matched) issues.push(`O material decidido para “${resolution.subject}” ainda não foi aplicado ao alvo desta entrega.`);
      }
      if (resolution.targetReferenceElementId) {
        const reference = scene.elements.find((element) => element.id === resolution.targetReferenceElementId);
        const matched = reference && scopedElements.some((element) => (
          element.materialId === reference.materialId
          && JSON.stringify(element.materialAssignments) === JSON.stringify(reference.materialAssignments)
        ));
        if (!matched) issues.push(`A aparência de referência decidida para “${resolution.subject}” ainda não foi reproduzida no alvo desta entrega.`);
      }
      // The absolute cota the plan decided (or amended) for a suspended element
      // is a deliverable of the step, verified against the built state like a
      // product or a dimension. Without it, an amended plan (1960 → 1560) left
      // the element where it was, the harness forced a capture of the unmoved
      // element and the review asked for yet another amendment (19/08 18:13).
      if (resolution.attribute === "position" || resolution.attribute === "relation") {
        const cotaDecisions = plan.spatialDecisions.filter((decision) => (
          (decision.subjectElementId && resolution.subjectElementId
            ? decision.subjectElementId === resolution.subjectElementId
            : planSubjectsMatch(decision.subject, resolution.subject))
          && (decision.bottomHeightMm != null || decision.topHeightMm != null)
        ));
        for (const decision of cotaDecisions) {
          for (const element of scopedElements) {
            const floorElevation = checkElementGrounded(scene, element).floorElevationMm;
            const [bottomAbs, topAbs] = renderedElementVerticalIntervalMm(element);
            const bottom = bottomAbs - floorElevation;
            const top = topAbs - floorElevation;
            const tolerance = 15;
            if (decision.bottomHeightMm != null && Math.abs(bottom - decision.bottomHeightMm) > tolerance) {
              issues.push(`A cota decidida para “${resolution.subject}” (face inferior a ${Math.round(decision.bottomHeightMm)} mm do piso) ainda não foi aplicada: o elemento está a ${Math.round(bottom)} mm. Reaplique a implantação com scene.element.place informando bottomHeightMm, ou use scene.element.nudge deltaMm [0, ${Math.round(decision.bottomHeightMm - bottom)}, 0], antes de revisar ou concluir.`);
            } else if (decision.bottomHeightMm == null && decision.topHeightMm != null && Math.abs(top - decision.topHeightMm) > tolerance) {
              issues.push(`A cota decidida para “${resolution.subject}” (topo a ${Math.round(decision.topHeightMm)} mm do piso) ainda não foi aplicada: o topo está a ${Math.round(top)} mm. Reaplique a implantação ou use scene.element.nudge deltaMm [0, ${Math.round(decision.topHeightMm - top)}, 0], antes de revisar ou concluir.`);
            }
          }
        }
      }
      if (resolution.attribute === "position" || resolution.attribute === "relation") {
        // The decided lateral relation (beside/against) is a deliverable like
        // the cota: on 20/08 13:12 the plan said "junto ao Roupeiro", nothing
        // measured it, and the step self-completed with the móvel 5 m away.
        const lateralDecisions = plan.spatialDecisions.filter((decision) => (
          (decision.relation === "beside" || decision.relation === "against")
          && (decision.subjectElementId && resolution.subjectElementId
            ? decision.subjectElementId === resolution.subjectElementId
            : planSubjectsMatch(decision.subject, resolution.subject))
        ));
        for (const decision of lateralDecisions) {
          const reference = (decision.referenceElementId
            ? scene.elements.find((candidate) => candidate.id === decision.referenceElementId)
            : undefined)
            ?? scene.elements.find((candidate) => planSubjectsMatch(candidate.name, decision.reference));
          if (!reference) continue;
          // O código não define o que "junto" significa: só confere quando o
          // PRÓPRIO plano declarou a folga (decisão criativa do projetista);
          // sem folga declarada, quem julga a distância é a revisão visual.
          if (!decision.clearanceMm) continue;
          for (const element of scopedElements) {
            if (element.id === reference.id) continue;
            const gap = horizontalGapBetweenElementsMm(element, reference);
            const allowance = decision.clearanceMm.maximum + 60;
            if (gap > allowance) {
              issues.push(`A folga lateral que o plano decidiu — “${resolution.subject}” ${decision.relation === "against" ? "encostado em" : "junto a"} “${decision.reference}”, ${decision.clearanceMm.minimum}–${decision.clearanceMm.maximum} mm — ainda não foi materializada: o vão horizontal atual é ${Math.round(gap)} mm. Aplique scene.element.place com relation=${decision.relation} e a referência real, ou emende a decisão se a folga planejada mudou.`);
            }
          }
        }
      }
      // A function decision ("close the doors", "leave the flap open") is a
      // deliverable of the step even when the element is never moved: it is
      // verified against the structured interaction state, not against the
      // step's mutation ledger. Without this, a step that merged "move the
      // base" and "close the wardrobe" completed as soon as the base passed —
      // and the doors stayed open (19/08 16:49).
      if (resolution.attribute === "function") {
        // The subject of a function decision is whatever element the plan
        // names — usually not the element this step created.
        const bySubject = scene.elements.filter((element) => planSubjectsMatch(element.name, resolution.subject));
        const targets = bySubject.length ? bySubject : scopedElements;
        for (const target of targets) {
          const interactions = this.loadedInteractions(target);
          if (!interactions) continue;
          const review = structuredFunctionReview(plan, target.name, interactions);
          if (review && !review.satisfied) {
            issues.push(`A função decidida para “${resolution.subject}” (${review.expected === "closed" ? "fechar" : "abrir"}) ainda não foi aplicada a “${target.name}”: ${review.evidence} Use scene.part.interact ou scene.part.setState antes de concluir a entrega.`);
          }
        }
      }
    }
    return [...new Set(issues)];
  }

  /** Interações dinâmicas (portas, gavetas, folhas) com o estado atual, quando o grafo do elemento já está em memória; null quando não há como ler. */
  loadedInteractions(element: SceneElement): Array<{ path: string; name: string; state: InteractionState }> | null {
    const graph = readLoadedDynamicGraph(element);
    return graph ? dynamicInteractions(graph, element) : null;
  }

  verifyStepProfessionalQuality(turnId: string, stepId: string): string[] {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan || plan.designFreedom === "strict") return [];
    const hasAgentVisualAuthorship = plan.designResolutions.some((resolution) => (
      resolution.stepId === stepId
      && resolution.authority === "agent"
      && ["position", "dimensions", "orientation", "material", "product", "relation", "appearance"].includes(resolution.attribute)
    ));
    if (!hasAgentVisualAuthorship) return [];

    const targetIds = this.professionalQualityTargetIds(turnId, stepId);
    // A planning/catalog-only step has no physical target to inspect. The
    // creative gate belongs to the step that actually mutates the scene.
    if (targetIds.length === 0) return [];
    const approved = this.approvedVisualIdsByTurn.get(turnId) ?? new Set<string>();
    return targetIds
      .filter((id) => !approved.has(id))
      .map((id) => `${id}: falta a revisão visual local dos critérios profissionais depois da última alteração.`);
  }

  professionalQualityState(turnId: string, stepId: string): {
    required: boolean;
    targetIds: string[];
    pendingIds: string[];
    complete: boolean;
  } {
    const plan = this.taskPlanByTurn.get(turnId);
    const required = Boolean(plan && plan.designFreedom !== "strict" && plan.designResolutions.some((resolution) => (
      resolution.stepId === stepId
      && resolution.authority === "agent"
      && ["position", "dimensions", "orientation", "material", "product", "relation", "appearance"].includes(resolution.attribute)
    )));
    const targetIds = required ? this.professionalQualityTargetIds(turnId, stepId) : [];
    const approved = this.approvedVisualIdsByTurn.get(turnId) ?? new Set<string>();
    const pendingIds = targetIds.filter((id) => !approved.has(id));
    return { required, targetIds, pendingIds, complete: required && targetIds.length > 0 && pendingIds.length === 0 };
  }

  professionalQualityCaptureIds(turnId: string, stepId: string): string[] {
    const plan = this.taskPlanByTurn.get(turnId);
    const scene = useEditorStore.getState().scene;
    const quality = this.professionalQualityState(turnId, stepId);
    if (!quality.required) return [];
    const referenceIds = plan?.designResolutions
      .filter((resolution) => resolution.stepId === stepId)
      .flatMap((resolution) => [
        resolution.targetReferenceElementId,
        ...resolution.reviewCriteria.map((criterion) => criterion.referenceElementId),
      ])
      .filter((id): id is string => typeof id === "string") ?? [];
    return [...new Set([...quality.pendingIds, ...quality.targetIds, ...referenceIds])]
      .filter((id) => Boolean(findSceneTargetName(scene, id)));
  }

  recordControlDecision(
    turnId: string,
    capability: string,
    input: unknown,
    result: unknown,
    failed = false,
  ): void {
    this.recordExecution(turnId, capability, input, result, 0, failed);
  }

  /**
   * Leitura do ambiente do turno: a registrada pela última chamada de
   * scene.environment.read ou, na falta dela, uma leitura nova da cena atual.
   * O validador do plano e o crítico usam sempre a cena real, não a cópia do
   * modelo.
   */
  environmentReading(turnId: string, focusElementIds?: ReadonlyArray<string>): EnvironmentReading {
    void turnId;
    return buildEnvironmentReading(useEditorStore.getState().scene, { focusElementIds: focusElementIds ?? null });
  }

  environmentReadDone(turnId: string): boolean {
    return this.environmentReadingByTurn.has(turnId);
  }

  prepareFinalVerification(turnId: string): { pendingIds: string[]; approvedIds: string[]; complete: boolean } {
    const all = this.mutatedElementIdsByTurn.get(turnId) ?? new Set<string>();
    const scene = useEditorStore.getState().scene;
    const existing = [...all].filter((id) => Boolean(findSceneTargetName(scene, id)));
    const approved = this.approvedVisualIdsByTurn.get(turnId) ?? new Set<string>();
    const approvedIds = existing.filter((id) => approved.has(id));
    const pendingIds = this.environmentApprovedTurns.has(turnId)
      ? existing.filter((id) => !approved.has(id))
      : existing;
    if (pendingIds.length) this.pendingVisualIdsByTurn.set(turnId, new Set(pendingIds));
    else this.pendingVisualIdsByTurn.delete(turnId);
    return {
      pendingIds,
      approvedIds,
      complete: existing.length === 0 || (this.environmentApprovedTurns.has(turnId) && pendingIds.length === 0),
    };
  }

  effectiveCapability(name: string, rawInput: unknown): CapabilityDefinition | undefined {
    if (name !== "capability.invoke") return getCapability(name);
    const input = parseCapabilityInput(name, rawInput) as { name: string };
    return getCapability(input.name);
  }

  describeConfirmation(name: string, rawInput: unknown): string {
    const input = parseCapabilityInput(name, rawInput) as RecordInput;
    const effective = this.effectiveCapability(name, rawInput);
    const effectiveInput = name === "capability.invoke"
      ? (input.arguments as RecordInput)
      : input;
    if (effective?.name === "scene.element.delete") {
      const ids = effectiveInput.elementIds as string[];
      const scene = useEditorStore.getState().scene;
      const targets = ids.map((id) => findSceneTargetName(scene, id) ?? id);
      return targets.length === 1
        ? `Apagar “${targets[0]}” da cena?`
        : `Apagar ${targets.length} itens da cena: ${targets.join(", ")}?`;
    }
    const identifiers = Object.entries(effectiveInput)
      .filter(([key, value]) => /Id$/.test(key) && typeof value === "string")
      .map(([, value]) => String(value));
    return `${effective?.title ?? "Executar a ação"}${identifiers.length ? ` em ${identifiers.join(", ")}` : ""}? Essa alteração não volta com Ctrl+Z.`;
  }

  requiresConfirmation(name: string, rawInput: unknown, turnId: string): boolean {
    const outerInput = parseCapabilityInput(name, rawInput) as RecordInput;
    const effective = this.effectiveCapability(name, rawInput);
    if (!effective) return true;
    const effectiveInput = name === "capability.invoke"
      ? outerInput.arguments
      : outerInput;
    return agentCallNeedsConfirmation({
      capability: effective.name,
      defaultConfirm: effective.confirm,
      arguments: effectiveInput,
      createdElementIds: createdElementIdsForTurn(turnId),
    });
  }

  async execute(name: string, rawInput: unknown, turnId: string, toolUseId?: string): Promise<unknown> {
    const definition = getCapability(name);
    if (!definition) throw new Error(`O modelo pediu uma capacidade que o CalcPlan não conhece: ${name}.`);
    const input = parseCapabilityInput(name, rawInput) as RecordInput;
    if (name === "capability.invoke") {
      const targetName = String(input.name);
      if (["capability.search", "capability.invoke"].includes(targetName)) {
        throw new Error("A descoberta de capacidades não pode chamar a si mesma.");
      }
      const result = await this.execute(targetName, input.arguments, turnId, toolUseId);
      return parseCapabilityOutput(name, { name: targetName, result });
    }

    const captureCount = this.viewportCapturesByTurn.get(turnId) ?? 0;

    if (GEOMETRY_DEPENDENT_CAPABILITIES.has(name) && typeof input.elementId === "string") {
      await this.requireCurrentRenderedGeometry(input.elementId);
    }

    const startedAt = performance.now();
    let output: unknown;
    try {
      // Keep policy rejections inside the audited execution boundary. A
      // rejected correction must be visible in the forensic log just like a
      // provider or capability failure.
      const correctionScope = this.correctionAllowedIdsByTurn.get(turnId);
      if (correctionScope && isVisualSceneMutation(definition)) {
        if (name === "scene.module.add" || name === "scene.module.install") {
          const liveRejectedIds = [...correctionScope]
            .filter((id) => Boolean(findSceneTargetName(useEditorStore.getState().scene, id)));
          if (liveRejectedIds.length) {
            throw new Error(`A revisão final já identificou os alvos que precisam de correção: ${liveRejectedIds.join(", ")}. Ajuste ou substitua deliberadamente esses elementos; não insira uma cópia enquanto eles permanecerem na cena.`);
          }
        }
        const targetIds = executionTargetIds(name, input);
        const forbidden = targetIds.filter((id) => !correctionScope.has(id));
        if (forbidden.length) {
          throw new Error(`A revisão final não autoriza alterar itens fora desta rodada: ${forbidden.join(", ")}. Use-os apenas como referência visual.`);
        }
      }
      const activeStepId = this.activeExecutionStepByTurn.get(turnId);
      const requiredAtomicPlacement = activeStepId
        ? this.requiredAtomicPlacementCapability(turnId, activeStepId)
        : null;
      if (requiredAtomicPlacement
        && ["scene.element.move", "scene.element.nudge", "scene.element.rotate"].includes(name)) {
        throw new Error(`Esta entrega possui uma relação espacial estruturada ainda não implantada. Use ${requiredAtomicPlacement} primeiro para aplicar posição, orientação, apoio e referência em uma única operação; ajustes locais só são permitidos depois dessa implantação.`);
      }
      switch (name) {
      case "capability.search":
        output = {
          capabilities: searchCapabilities(String(input.query), Number(input.limit)).map((capability) => ({
            name: capability.name,
            title: capability.title,
            group: capability.group,
            plane: capability.plane,
            effect: capability.effect,
            confirm: capability.confirm,
            summary: capability.summary,
            guide: operationalGuideForCapability(capability),
            examples: capability.examples ?? [],
            inputSchema: capabilityToolDefinition(capability).inputSchema,
          })),
        };
        break;
      case "scene.read":
        output = readScene(useEditorStore.getState().scene, String(input.detail));
        break;
      case "scene.environment.read": {
        const reading = buildEnvironmentReading(useEditorStore.getState().scene, {
          roomId: typeof input.roomId === "string" ? input.roomId : null,
          wallIds: Array.isArray(input.wallIds) ? input.wallIds.map(String) : null,
          focusElementIds: Array.isArray(input.focusElementIds) ? input.focusElementIds.map(String) : null,
        });
        this.environmentReadingByTurn.set(turnId, reading);
        output = reading;
        break;
      }
      case "scene.selection.set":
        output = setSelection(input.elementIds as string[]);
        break;
      case "scene.selection.read":
        output = { selectedIds: useEditorStore.getState().selectedIds };
        break;
      case "scene.module.add":
        output = this.addModule(input, turnId);
        break;
      case "scene.module.install":
        output = await this.installModule(input, turnId);
        break;
      case "scene.element.move":
        output = moveElement(input, turnId);
        break;
      case "scene.element.nudge":
        output = nudgeElement(input, turnId);
        break;
      case "scene.element.mount":
        output = mountElement(input, turnId);
        break;
      case "scene.element.rotate":
        output = rotateElement(input, turnId);
        break;
      case "scene.element.resize":
        output = await this.resizeElement(input, turnId);
        break;
      case "scene.element.paint":
        output = this.paintElements(input, turnId);
        break;
      case "scene.element.appearance.match":
        output = matchElementAppearance(input, turnId);
        break;
      case "scene.element.delete":
        output = this.deleteElements(input, turnId);
        break;
      case "scene.element.read":
        output = await this.readElement(input);
        break;
      case "scene.check.clearance": {
        const scene = useEditorStore.getState().scene;
        const element = findElement(String(input.elementId), scene);
        output = { ...checkElementClearance(scene, element), geometry: renderedGeometryEvidence(element) };
        break;
      }
      case "scene.check.grounded": {
        const scene = useEditorStore.getState().scene;
        const element = findElement(String(input.elementId), scene);
        output = { ...checkElementGrounded(scene, element), geometry: renderedGeometryEvidence(element) };
        break;
      }
      case "scene.check.installation": {
        const scene = useEditorStore.getState().scene;
        const element = findElement(String(input.elementId), scene);
        output = { ...checkElementInstallation(scene, element), geometry: renderedGeometryEvidence(element) };
        break;
      }
      case "scene.placement.evaluate":
        output = this.evaluatePlacementCandidates(input);
        break;
      case "scene.placement.resolve":
        output = this.resolveSemanticPlacement(input, turnId);
        break;
      case "scene.element.place":
        output = this.placeElementByRelation(input, turnId);
        break;
      case "scene.element.duplicate":
        output = duplicateElements(input, turnId);
        break;
      case "scene.part.interact":
        output = await this.interactPart(input, turnId);
        break;
      case "scene.part.setState":
        output = await this.setPartGroupState(input, turnId);
        break;
      case "scene.wall.add":
        output = addWall(input, turnId);
        break;
      case "scene.wall.update":
        output = updateWall(input, turnId);
        break;
      case "scene.wall.vertex.move":
        output = moveWallVertex(input, turnId);
        break;
      case "scene.opening.add":
        output = addOpening(input, turnId);
        break;
      case "scene.opening.update":
        output = updateOpening(input, turnId);
        break;
      case "scene.primitive.add":
        output = addPrimitive(input, turnId);
        break;
      case "scene.measurement.add":
        output = addMeasurement(input, turnId);
        break;
      case "scene.measurement.update":
        output = updateMeasurement(input, turnId);
        break;
      case "scene.layer.add":
        output = addLayer(input, turnId);
        break;
      case "scene.layer.update":
        output = updateLayer(input, turnId);
        break;
      case "scene.layer.reorder":
        output = reorderLayer(input, turnId);
        break;
      case "scene.layer.delete":
        output = deleteLayer(input, turnId);
        break;
      case "scene.operation.add":
        output = addOperation(input, turnId);
        break;
      case "scene.operation.remove":
        output = removeOperation(input, turnId);
        break;
      case "scene.part.attribute.set":
        output = setPartAttribute(input, turnId);
        break;
      case "scene.camera.set":
        output = setCamera(input);
        break;
      case "scene.view.mode.set":
        useEditorStore.getState().setMode(input.mode as "plan" | "front" | "3d");
        output = { mode: useEditorStore.getState().mode };
        break;
      case "scene.viewport.capture": {
        const scene = useEditorStore.getState().scene;
        const globalPendingIds = [...(this.pendingVisualIdsByTurn.get(turnId) ?? [])]
          .filter((id) => Boolean(findSceneTargetName(scene, id)));
        const activeStepId = this.activeExecutionStepByTurn.get(turnId);
        const stepPendingIds = input.context === "targets" && activeStepId
          ? this.professionalQualityState(turnId, activeStepId).pendingIds
          : [];
        const pendingIds = stepPendingIds.length ? stepPendingIds : globalPendingIds;
        const requestedFit = (input.fit as string[])
          .filter((id) => Boolean(findSceneTargetName(scene, id)));
        const requestedPending = pendingIds.length
          ? requestedFit.filter((id) => pendingIds.includes(id))
          : requestedFit;
        const reviewTargetIds = requestedPending.length ? requestedPending : pendingIds.slice(0, 3);
        // References used to judge alignment, proportion and spacing must stay
        // in the frame even though only changed targets require approval.
        // Filtering the fit down to pending IDs made the critic inspect the
        // moved cabinet without the wardrobe it had to align with. A suspended
        // target additionally brings the nearest suspended neighbour of the
        // same wall into the frame: the line it should follow (or deliberately
        // break) has to be visible to be judged.
        const framingIds = [...new Set([
          ...reviewTargetIds,
          ...requestedFit,
          ...this.sameFamilyNeighbours(scene, reviewTargetIds.length ? reviewTargetIds : requestedFit),
        ])].slice(0, 8);
        const requestedContext = input.context as "targets" | "environment";
        const currentEpoch = this.visualEpochByTurn.get(turnId) ?? 0;
        const evidence = this.viewportEvidenceByTurn.get(turnId) ?? new Map<string, ViewportCaptureEvidence>();
        const sameContextEvidence = [...evidence.values()].filter((candidate) => (
          candidate.epoch === currentEpoch && candidate.context === requestedContext
        ));
        // The global packet supplies room context; it does not invalidate the
        // detailed, current-epoch views already approved for each changed
        // target. Reuse those views for target coverage while keeping the
        // panorama/plan/facade requirement exclusive to environment captures.
        const coverageEvidence = coverageEvidenceForCapture(evidence.values(), currentEpoch, requestedContext);
        const recoveryView = shouldUseAdaptiveRecovery({
          context: requestedContext,
          view: String(input.view),
          hasExplicitDirection: input.azimuthDeg !== undefined || input.elevationDeg !== undefined,
          environmentOverviewComplete: sameContextEvidence.some((candidate) => candidate.overviewComplete),
        })
          ? nextVisualRecoveryView(reviewTargetIds.length ? reviewTargetIds : framingIds, coverageEvidence.flatMap((candidate) => candidate.views))
          : null;
        // The first packet keeps references in frame so alignment and scale can
        // be judged. A recovery packet has a different job: obtain a second
        // legible angle of the still-pending target. Refitting the whole group
        // on every retry made a small cabinet occupy 2–5% of the image beside a
        // full-height wardrobe, so every nominally new angle failed the same
        // readability threshold.
        const captureFitIds = visualRecoveryFitIds(framingIds, reviewTargetIds, Boolean(recoveryView));
        // A repeated adaptive request must explore a genuinely new direction.
        // Otherwise the deterministic camera scorer returns the same frame and
        // no amount of retries can create independent evidence.
        const capture = await captureEditorViewport({
          fit: captureFitIds,
          reviewTargetIds: reviewTargetIds.length ? reviewTargetIds : captureFitIds,
          context: requestedContext,
          view: input.view as "adaptive" | "3d" | "plan" | "front",
          maxWidth: Number(input.maxWidth),
          maxImages: Number(input.maxImages),
          occlusionMode: input.occlusionMode as "auto" | "none" | "hide-walls",
          ...(typeof input.azimuthDeg === "number"
            ? { azimuthDeg: input.azimuthDeg }
            : recoveryView ? { azimuthDeg: recoveryView.azimuthDeg } : {}),
          ...(typeof input.elevationDeg === "number"
            ? { elevationDeg: input.elevationDeg }
            : recoveryView ? { elevationDeg: recoveryView.elevationDeg } : {}),
          distanceScale: recoveryView
            ? Math.max(Number(input.distanceScale), recoveryView.distanceScale)
            : Number(input.distanceScale),
        });
        const currentViews = [capture.camera, ...capture.additionalImages.map((image) => image.camera)];
        // Coverage is accounted for every target still pending in this epoch,
        // not only for the targets this capture happened to frame: a directed
        // recapture of one target must be able to complete the packet that an
        // earlier capture left short, instead of reporting the rest as missing.
        const coverageTargets = pendingIds.length ? pendingIds : reviewTargetIds;
        const accumulatedReview = accumulatedVisualCoverage(
          [...new Set([...coverageTargets, ...(reviewTargetIds.length ? reviewTargetIds : framingIds)])],
          [...coverageEvidence.flatMap((candidate) => candidate.views), ...currentViews],
          capture.visualReview,
          coverageEvidence.length + 1,
        );
        const covered = new Set(accumulatedReview.coveredIds);
        const remaining = coverageTargets.filter((id) => !covered.has(id));
        evidence.set(capture.captureId, {
          captureId: capture.captureId,
          epoch: currentEpoch,
          targetIds: capture.fittedIds,
          coveredIds: accumulatedReview.coveredIds,
          context: capture.context,
          overviewComplete: capture.context !== "environment" || Boolean(
            sameContextEvidence.some((candidate) => candidate.overviewComplete)
            || (capture.overview?.environment
              && capture.overview.hasPerspective
              && capture.overview.hasPlan
              && capture.overview.hasFront)
          ),
          views: currentViews,
        });
        this.viewportEvidenceByTurn.set(turnId, evidence);
        if (toolUseId) {
          const aliases = this.viewportCaptureAliasByTurn.get(turnId) ?? new Map<string, string>();
          aliases.set(toolUseId, capture.captureId);
          this.viewportCaptureAliasByTurn.set(turnId, aliases);
        }
        this.viewportCapturesByTurn.set(turnId, captureCount + 1);
        const automaticRejectedIds = capture.inspection
          .filter((entry) => entry.support.status === "gap" || entry.support.status === "penetrating")
          .map((entry) => entry.elementId)
          .filter((id) => coverageTargets.includes(id));
        if (capture.context === "environment" && automaticRejectedIds.length) {
          this.correctionAllowedIdsByTurn.set(turnId, new Set(automaticRejectedIds));
        }
        output = {
          ...capture,
          visualReview: accumulatedReview,
          pendingIdsBeforeCapture: pendingIds,
          coverageComplete: remaining.length === 0,
        };
        break;
      }
      case "scene.viewport.review": {
        const requestedCaptureId = String(input.captureId);
        const currentEpoch = this.visualEpochByTurn.get(turnId) ?? 0;
        const evidenceById = this.viewportEvidenceByTurn.get(turnId) ?? new Map<string, ViewportCaptureEvidence>();
        const currentCaptureIds = [...evidenceById.values()]
          .filter((candidate) => candidate.epoch === currentEpoch)
          .map((candidate) => candidate.captureId);
        const resolvedCaptureId = resolveViewportCaptureReference(
          requestedCaptureId,
          currentCaptureIds,
          this.viewportCaptureAliasByTurn.get(turnId),
        );
        const resolvedEvidence = resolvedCaptureId ? evidenceById.get(resolvedCaptureId) : undefined;
        if (!resolvedEvidence) throw new Error("A captura informada não pertence a este turno ou já ficou desatualizada.");
        // The ensemble judgment belongs to the panorama of the epoch, whichever
        // capture id the critic keyed it to: a directed recapture only completes
        // the per-target evidence of the packet the panorama opened. Reviewing
        // the panorama through its latest recapture therefore repairs to the
        // environment capture instead of silently dropping the environment
        // verdict (and with it the professional review of the whole).
        const epochEnvironmentEvidence = [...evidenceById.values()]
          .filter((candidate) => candidate.epoch === resolvedEvidence.epoch && candidate.context === "environment")
          .at(-1);
        const evidence = resolvedEvidence.context === "targets"
          && input.environment
          && epochEnvironmentEvidence
          ? epochEnvironmentEvidence
          : resolvedEvidence;
        const accepted = this.approvedVisualIdsByTurn.get(turnId) ?? new Set<string>();
        // Coverage accrued by every capture of this epoch (the panorama packet
        // plus directed recaptures) backs the verdicts of this review.
        const epochCoveredIds = new Set([...evidenceById.values()]
          .filter((candidate) => candidate.epoch === evidence.epoch)
          .flatMap((candidate) => candidate.coveredIds));
        evidence.coveredIds.forEach((id) => epochCoveredIds.add(id));
        const rejectedIds: string[] = [];
        const unverifiableIds: string[] = [];
        const issues: string[] = [];
        const protectedObservations: string[] = [];
        const required = [...(this.mutatedElementIdsByTurn.get(turnId) ?? [])]
          .filter((id) => Boolean(findSceneTargetName(useEditorStore.getState().scene, id)));
        const submittedVerdictIds = new Set((input.verdicts as RecordInput[]).map((verdict) => String(verdict.elementId)));
        const environmentEvidence = professionalEnvironmentEvidence({
          overviewComplete: evidence.context !== "environment" || evidence.overviewComplete,
          capturedTargetIds: evidence.context === "environment" ? evidence.targetIds : [],
          changedIds: new Set(required),
          verdictIds: submittedVerdictIds,
        });
        const missingCurrentVerdicts = environmentEvidence.missingVerdictIds;
        for (const elementId of missingCurrentVerdicts) {
          accepted.delete(elementId);
          unverifiableIds.push(elementId);
        }
        issues.push(...environmentEvidence.issues);
        for (const rawVerdict of input.verdicts as RecordInput[]) {
          const elementId = String(rawVerdict.elementId);
          const coverageDisposition = uncoveredVisualVerdictDisposition(
            accepted.has(elementId),
            epochCoveredIds.has(elementId),
          );
          if (coverageDisposition === "preserve-approval") {
            protectedObservations.push(`${findSceneTargetName(useEditorStore.getState().scene, elementId) ?? elementId}: a aprovação anterior foi preservada porque a captura atual não contém evidência nova deste alvo.`);
            continue;
          }
          if (coverageDisposition === "remain-pending") {
            if (!unverifiableIds.includes(elementId)) unverifiableIds.push(elementId);
            continue;
          }
          const checks = { ...(rawVerdict.checks as RecordInput) };
          const reportedIssues = Array.isArray(rawVerdict.issues)
            ? rawVerdict.issues.filter((issue): issue is string => typeof issue === "string")
            : [];
          const designAssessments = Array.isArray(rawVerdict.designAssessments)
            ? rawVerdict.designAssessments.filter((assessment): assessment is RecordInput => Boolean(assessment && typeof assessment === "object"))
            : [];
          const automaticIssues = this.independentReviewIssues(turnId, elementId);
          const useReview = this.independentUseReview(turnId, elementId);
          automaticIssues.push(...useReview.issues);
          const plan = this.taskPlanByTurn.get(turnId);
          const elementName = findSceneTargetName(useEditorStore.getState().scene, elementId) ?? elementId;
          for (const observation of useReview.observations) protectedObservations.push(`${elementName}: ${observation}`);
          for (const observation of this.fixtureObservations(elementId)) protectedObservations.push(observation);
          const functionReview = await this.structuredFunctionReviewForElement(turnId, elementId);
          let correctedFunctionContradiction = false;
          if (functionReview?.satisfied && checks.function === "fail") {
            checks.function = "pass";
            correctedFunctionContradiction = true;
            protectedObservations.push(`${elementName}: o estado funcional estruturado prevaleceu sobre a leitura visual (${functionReview.evidence})`);
          } else if (functionReview && !functionReview.satisfied) {
            automaticIssues.push(`O estado funcional esperado como ${functionReview.expected === "closed" ? "fechado" : "aberto"} não foi comprovado. ${functionReview.evidence}`);
          }
          const failedChecks = Object.entries(checks)
            .filter(([, value]) => value === "fail")
            .map(([key]) => key);
          const localStepId = evidence.context === "targets" ? this.activeExecutionStepByTurn.get(turnId) : undefined;
          const stepScope = localStepId ? this.stepDesignScope(turnId, elementId, elementName, localStepId) : null;
          const requiredDesignAspects = stepScope?.applies
            ? stepScope.aspects
            : this.requiredDesignAspectsForElement(turnId, elementId, elementName);
          const designRequired = stepScope?.applies
            ? stepScope.agentAuthored
            : elementRequiresAgentDesignReview(plan, elementName) || requiredDesignAspects.length > 0;
          const assessedDesignAspects = new Set(designAssessments.map((assessment) => String(assessment.aspect)));
          const missingDesignAspects = requiredDesignAspects.filter((aspect) => !assessedDesignAspects.has(aspect));
          const failedDesignAssessments = designAssessments.filter((assessment) => assessment.status === "fail");
          if (designRequired && requiredDesignAspects.length === 0) {
            automaticIssues.push("O plano autoral não forneceu critérios profissionais estruturados para este alvo.");
          }
          if (missingDesignAspects.length) {
            automaticIssues.push(`Faltou avaliar os critérios profissionais: ${missingDesignAspects.join(", ")}.`);
          }
          // A datum the plan declared and the environment gate validated (high
          // storage on an existing line, a justified new line) is a design
          // decision, not an open question for the critic: failing it again by
          // reach, line or gap re-litigates what the plan decided and swings the
          // element between two opposite objections (1960 -> 1600 -> 1960 -> 1640
          // on 19/08 18:36). Likewise, a "finish does not match" read of the
          // image cannot beat the structured material that does match.
          const datumProtection = this.declaredDatumProtection(turnId, elementId, useReview.issues.length === 0);
          const materialProtection = this.structuredMaterialProtection(turnId, elementId);
          // The element is back on a vertical band a previous verdict already
          // rejected: the critic has been asking for opposite moves. A third
          // verdict on that band may observe, not reject, on the aspects that
          // drove the swing — otherwise the executor chases the verdicts forever.
          const oscillation = this.reviewOscillation(turnId, elementId);
          let protectedAssessments = 0;
          for (const assessment of failedDesignAssessments) {
            const aspect = String(assessment.aspect);
            if (oscillation && DATUM_PROTECTED_ASPECTS.has(aspect)) {
              protectedAssessments += 1;
              protectedObservations.push(`${elementName}: avaliação de ${aspect} registrada como observação — gangorra detectada: ${oscillation}. A cota permanece com a decisão registrada no plano; registre a ressalva sem pedir o movimento oposto de novo. ${String(assessment.evidence)}`);
              continue;
            }
            if (datumProtection && DATUM_PROTECTED_ASPECTS.has(aspect)) {
              protectedAssessments += 1;
              protectedObservations.push(`${elementName}: avaliação de ${aspect} registrada como observação — a cota segue a linha/premissa declarada e validada no plano (${datumProtection}). ${String(assessment.evidence)}`);
              continue;
            }
            if (materialProtection && (aspect === "material" || /acabamento|material|\bcor\b|tonalidade/i.test(String(assessment.evidence)))) {
              protectedAssessments += 1;
              protectedObservations.push(`${elementName}: avaliação de ${aspect} registrada como observação — o material estruturado coincide com a referência decidida (${materialProtection}); a leitura de tom na imagem não prevalece sobre o dado. ${String(assessment.evidence)}`);
              continue;
            }
            automaticIssues.push(`Falha de ${aspect}: ${String(assessment.evidence)} Correção: ${String(assessment.correction ?? "não informada")}.`);
          }
          // Acabamento divergente se corrige no elemento, não no plano: sem
          // esta dica o executor gastou três emendas antes do appearance.match
          // (20/08 16:15).
          if (failedDesignAssessments.some((assessment) => String(assessment.aspect) === "material" || /acabamento|material|\bcor\b|tonalidade/i.test(String(assessment.evidence)))
            && !materialProtection) {
            automaticIssues.push("Para igualar o acabamento ao da referência, use scene.element.appearance.match (ou scene.element.paint com o materialId decidido) no próprio elemento; task.plan.amend não altera material.");
          }
          const allFailuresProtected = protectedAssessments > 0 && protectedAssessments === failedDesignAssessments.length;
          let correctedDesignContradiction = false;
          if (allFailuresProtected && checks.design === "fail") {
            checks.design = "pass";
            correctedDesignContradiction = true;
          }
          if (allFailuresProtected && checks.geometry === "fail" && (datumProtection || oscillation) && automaticIssues.length === 0) {
            // The only geometry complaint was the protected cota itself.
            checks.geometry = "pass";
          }
          // Read after protection: a design check that failed only on protected
          // aspects has been corrected above and must not block the approval
          // (19/08 19:34 — the protected aéreo was still rejected by this rule).
          if (designRequired && checks.design !== "pass") {
            automaticIssues.push("A autoria guiada ou aberta exige avaliação explícita de design.");
          }
          const failedChecksAfterProtection = Object.entries(checks)
            .filter(([, value]) => value === "fail")
            .map(([key]) => key);
          const effectiveStatus = (correctedFunctionContradiction || correctedDesignContradiction)
            && failedChecksAfterProtection.length === 0
            && automaticIssues.length === 0
            ? "approved"
            : rawVerdict.status;
          const canApprove = epochCoveredIds.has(elementId)
            && effectiveStatus === "approved"
            && checks.visibility === "pass"
            && failedChecksAfterProtection.length === 0
            && automaticIssues.length === 0;
          const verdictSummary = typeof rawVerdict.summary === "string" ? rawVerdict.summary : "";
          const recordVerdict = (verdict: ReviewLedgerEntry["verdict"]) => this.recordReviewVerdict(turnId, {
            captureId: evidence.captureId,
            elementId,
            elementName,
            verdict,
            failedAspects: failedDesignAssessments
              .map((assessment) => String(assessment.aspect))
              .filter((aspect) => !((datumProtection || oscillation) && DATUM_PROTECTED_ASPECTS.has(aspect))),
            corrections: failedDesignAssessments
              .map((assessment) => (typeof assessment.correction === "string" ? assessment.correction.slice(0, 200) : null))
              .filter((text): text is string => Boolean(text))
              .slice(0, 4),
            summary: verdictSummary.slice(0, 240),
          });
          if (canApprove) {
            accepted.add(elementId);
            recordVerdict("approved");
            continue;
          }
          accepted.delete(elementId);
          if (automaticIssues.length > 0 || failedChecksAfterProtection.length > 0 || effectiveStatus === "rejected") {
            rejectedIds.push(elementId);
            recordVerdict("rejected");
          } else if (effectiveStatus === "unverifiable" || !epochCoveredIds.has(elementId)) {
            unverifiableIds.push(elementId);
            recordVerdict("unverifiable");
          } else {
            rejectedIds.push(elementId);
            recordVerdict("rejected");
          }
          const summary = verdictSummary || "Laudo visual reprovado.";
          issues.push(`${elementId}: ${[summary, ...reportedIssues, ...failedChecksAfterProtection.map((check) => `falha em ${check}`), ...automaticIssues].join("; ")}`);
        }
        let environmentStatus: "approved" | "rejected" | "unverifiable" | "not_requested" = "not_requested";
        if (evidence.context === "environment") {
          const environment = input.environment as RecordInput | undefined;
          if (!environment) throw new Error("A captura panorâmica exige o laudo environment antes do fechamento.");
          const environmentChecks = environment.checks as RecordInput;
          const rawEnvironmentIssues = Array.isArray(environment.issues) ? environment.issues as RecordInput[] : [];
          const professionalJudgment = environment.professionalJudgment as RecordInput;
          const comparedChangedIds = Array.isArray(professionalJudgment.comparedChangedIds)
            ? professionalJudgment.comparedChangedIds.map(String)
            : [];
          const issueElementIds = rawEnvironmentIssues.map((issue) => String(issue.elementId));
          const judgmentIssues = professionalEnvironmentJudgmentIssues({
            plan: this.taskPlanByTurn.get(turnId),
            changedIds: required,
            comparedChangedIds,
            checks: environmentChecks,
            wouldReviseBeforePresentation: professionalJudgment.wouldReviseBeforePresentation === true,
            issueElementIds,
            judgmentText: [professionalJudgment.rationale, professionalJudgment.strongestConcern, environment.summary]
              .filter((text): text is string => typeof text === "string").join(" "),
            ergonomicsRelevant: required.some((id) => {
              const profile = this.reviewTargetProfile(id);
              return Boolean(profile && (profile.suspended || ["countertop", "base_cabinet", "table", "seating", "bed", "low_furniture"].includes(profile.kind)));
            }),
          });
          const failedEnvironmentChecks = Object.entries(environmentChecks)
            .filter(([, value]) => value === "fail")
            .map(([key]) => key);
          if (environment.status === "rejected" && rawEnvironmentIssues.length === 0) {
            throw new Error("Uma reprovação panorâmica precisa atribuir cada problema a um alvo alterado.");
          }
          if (failedEnvironmentChecks.length && rawEnvironmentIssues.length === 0) {
            throw new Error("Cada verificação panorâmica reprovada precisa identificar o elemento e o problema correspondente.");
          }
          if (professionalJudgment.wouldReviseBeforePresentation === true && rawEnvironmentIssues.length === 0) {
            throw new Error("O julgamento profissional ainda recomenda revisão, portanto precisa atribuir evidência e correção a um alvo alterado.");
          }
          if (professionalJudgment.wouldReviseBeforePresentation !== true && rawEnvironmentIssues.length > 0) {
            throw new Error("O laudo contém correções, mas o julgamento profissional declarou que não revisaria o resultado. Corrija a contradição.");
          }
          issues.push(...judgmentIssues);
          if (!environmentEvidence.ready) {
            environmentStatus = "unverifiable";
            this.environmentApprovedTurns.delete(turnId);
            evidence.targetIds.filter((id) => required.includes(id)).forEach((id) => accepted.delete(id));
          } else if (environment.status === "unverifiable" || environmentChecks.overviewVisibility !== "pass") {
            environmentStatus = "unverifiable";
            this.environmentApprovedTurns.delete(turnId);
            required.forEach((id) => accepted.delete(id));
          } else {
            const scene = useEditorStore.getState().scene;
            let actionableEnvironmentIssues = 0;
            for (const rawIssue of rawEnvironmentIssues) {
              const issue = rawIssue as unknown as EnvironmentReviewIssue;
              const elementName = findSceneTargetName(scene, issue.elementId) ?? issue.elementId;
              if (issue.attribute === "function") {
                const functionReview = await this.structuredFunctionReviewForElement(turnId, issue.elementId);
                if (functionReview?.satisfied) {
                  protectedObservations.push(`${elementName}: ${issue.summary} (estado funcional estruturado comprovado: ${functionReview.evidence})`);
                  continue;
                }
              }
              const disposition = environmentIssueDisposition(
                this.taskPlanByTurn.get(turnId),
                issue,
                elementName,
                new Set(required),
                this.designAuthorityForElement(turnId, issue.elementId, issue.attribute),
              );
              const evidenceText = typeof rawIssue.evidence === "string" ? ` Evidência: ${rawIssue.evidence}` : "";
              const correctionText = typeof rawIssue.correction === "string" ? ` Correção: ${rawIssue.correction}` : "";
              const observation = `${elementName}: ${issue.summary}.${evidenceText}${correctionText}`;
              if (!disposition.actionable) {
                protectedObservations.push(`${observation} (${disposition.reason})`);
                continue;
              }
              issues.push(observation);
              actionableEnvironmentIssues += 1;
              if (!rejectedIds.includes(issue.elementId)) rejectedIds.push(issue.elementId);
              accepted.delete(issue.elementId);
            }
            const missingProfessionalComparison = required.some((id) => !comparedChangedIds.includes(id));
            environmentStatus = missingProfessionalComparison
              ? "unverifiable"
              : actionableEnvironmentIssues || judgmentIssues.length
                ? "rejected"
                : "approved";
            if (environmentStatus === "approved") this.environmentApprovedTurns.add(turnId);
            else this.environmentApprovedTurns.delete(turnId);
          }
        }
        this.approvedVisualIdsByTurn.set(turnId, accepted);
        const remainingIds = required.filter((id) => !accepted.has(id));
        if (remainingIds.length) this.pendingVisualIdsByTurn.set(turnId, new Set(remainingIds));
        else this.pendingVisualIdsByTurn.delete(turnId);
        // Only the final panoramic review may lock correction to rejected
        // elements. A target/step review must never deadlock an active step by
        // excluding the element that still needs work.
        if (evidence.context === "environment") {
          if (rejectedIds.length) this.correctionAllowedIdsByTurn.set(turnId, new Set(rejectedIds));
          else this.correctionAllowedIdsByTurn.delete(turnId);
        }
        output = {
          captureId: evidence.captureId,
          ...(requestedCaptureId !== evidence.captureId ? { requestedCaptureId, captureReferenceRepaired: true } : {}),
          acceptedIds: [...accepted],
          rejectedIds,
          unverifiableIds,
          remainingIds,
          coverageComplete: (evidence.context !== "environment" || environmentStatus === "approved")
            && remainingIds.length === 0
            && rejectedIds.length === 0
            && unverifiableIds.length === 0,
          issues,
          environmentStatus,
          // Turn-level state the harness needs to decide whether the final
          // verification may close: the panorama of this epoch and whether
          // its ensemble judgment has already been approved.
          environmentApproved: this.environmentApprovedTurns.has(turnId),
          ...(epochEnvironmentEvidence ? { environmentCaptureId: epochEnvironmentEvidence.captureId } : {}),
          protectedObservations,
        };
        break;
      }
      case "scene.undo": {
        const possible = useEditorStore.getState().history.length > 0;
        useEditorStore.getState().undo();
        output = { undone: possible };
        break;
      }
      case "scene.redo": {
        const possible = useEditorStore.getState().future.length > 0;
        useEditorStore.getState().redo();
        output = { redone: possible };
        break;
      }
      case "scene.save":
        await useEditorStore.getState().save();
        output = { saved: useEditorStore.getState().saveState === "saved", revision: useEditorStore.getState().revision };
        break;
      case "project.rename.open":
        withAgentTurn(turnId, () => useEditorStore.getState().renameProject(String(input.name)));
        output = { name: useEditorStore.getState().project?.name ?? String(input.name) };
        break;
      case "catalog.item.search":
        output = await this.searchCatalog(input);
        break;
      case "catalog.folder.list":
        output = await listCatalogFolders(input);
        break;
      case "ui.ask":
        throw new Error("ui.ask é atendida pela conversa, não pelo editor.");
      default:
        if (name === "job.wait") output = await this.waitForJob(input, turnId);
        else if (definition.handler?.kind === "rest") output = await this.executeRest(definition.handler, input, turnId);
        else throw new Error(`A capacidade ${name} ainda não tem executor no editor.`);
      }

      if (GEOMETRY_MUTATION_CAPABILITIES.has(name)) {
        const ids = executionElementIds(input, output);
        await Promise.all(ids.map((id) => this.awaitCurrentRenderedGeometry(id)));
        output = refreshElementSnapshots(output);
      }

      const parsed = parseCapabilityOutput(name, output);
      if (isVisualSceneMutation(definition) && mutationResultChanged(parsed)) {
        this.visualEpochByTurn.set(turnId, (this.visualEpochByTurn.get(turnId) ?? 0) + 1);
        this.environmentApprovedTurns.delete(turnId);
        const ids = executionElementIds(input, parsed);
        if (ids.length) {
          const pending = this.pendingVisualIdsByTurn.get(turnId) ?? new Set<string>();
          const all = this.mutatedElementIdsByTurn.get(turnId) ?? new Set<string>();
          const approved = this.approvedVisualIdsByTurn.get(turnId);
          ids.forEach((id) => {
            pending.add(id);
            all.add(id);
            approved?.delete(id);
            this.correctionAllowedIdsByTurn.get(turnId)?.add(id);
          });
          const outputStepId = parsed && typeof parsed === "object" && !Array.isArray(parsed)
            && typeof (parsed as RecordInput).stepId === "string"
            ? String((parsed as RecordInput).stepId)
            : null;
          const plan = this.taskPlanByTurn.get(turnId);
          const stepId = this.mutationStepId(turnId, name, input, ids, outputStepId, plan);
          if (stepId) {
            const byStep = this.mutatedElementIdsByStepByTurn.get(turnId) ?? new Map<string, Set<string>>();
            const stepIds = byStep.get(stepId) ?? new Set<string>();
            ids.forEach((id) => stepIds.add(id));
            byStep.set(stepId, stepIds);
            this.mutatedElementIdsByStepByTurn.set(turnId, byStep);
            const records = this.stepMutationEvidenceByTurn.get(turnId) ?? [];
            records.push({ stepId, capability: name, elementIds: [...ids] });
            this.stepMutationEvidenceByTurn.set(turnId, records);
          }
          this.pendingVisualIdsByTurn.set(turnId, pending);
          this.mutatedElementIdsByTurn.set(turnId, all);
        }
      }
      const result = addAutomaticVerification(definition, input, parsed);
      assertResultBudget(result);
      this.recordExecution(turnId, definition.name, input, result, performance.now() - startedAt, false);
      return result;
    } catch (error) {
      this.recordExecution(turnId, definition.name, input, { error: error instanceof Error ? error.message : "Falha" }, performance.now() - startedAt, true);
      throw error;
    }
  }

  private async awaitCurrentRenderedGeometry(elementId: string): Promise<void> {
    const element = useEditorStore.getState().scene.elements.find((candidate) => candidate.id === elementId);
    if (!element || !dynamicAssetIdFor(element)) return;
    await waitForRenderedElementEnvelope(element);
  }

  private async requireCurrentRenderedGeometry(elementId: string): Promise<void> {
    const element = findElement(elementId);
    if (!dynamicAssetIdFor(element)) return;
    const state = await waitForRenderedElementEnvelope(element);
    if (state.status !== "ready") {
      throw new Error(`A geometria física de “${element.name}” ainda está sendo sincronizada para a revisão ${element.revision}. Aguarde a renderização atual antes de medir, implantar ou corrigir o elemento; não use o envelope paramétrico como se fosse a malha medida.`);
    }
  }

  private mutationStepId(
    turnId: string,
    capability: string,
    input: RecordInput,
    elementIds: string[],
    outputStepId: string | null,
    plan: TaskPlan | undefined,
  ): string | undefined {
    if (outputStepId && plan?.steps.some((step) => step.id === outputStepId)) return outputStepId;

    // A refinement performed while a later step is active belongs to that
    // step when the step planned exactly this kind of change for this subject
    // (install in step A, unify the finish in step B). Crediting it to the
    // creating step made step B unable to ever prove its own work.
    const activeStepId = this.activeExecutionStepByTurn.get(turnId);
    if (activeStepId && plan) {
      const scene = useEditorStore.getState().scene;
      const activeStepOwnsChange = (plan.designResolutions ?? []).some((resolution) => (
        resolution.stepId === activeStepId
        && mutationSupportsDesignAttribute(capability, resolution.attribute)
        && elementIds.some((id) => {
          if (resolution.subjectElementId && id === resolution.subjectElementId) return true;
          const element = scene.elements.find((candidate) => candidate.id === id);
          return Boolean(element && planSubjectsMatch(element.name, resolution.subject));
        })
      ));
      if (activeStepOwnsChange) return activeStepId;
    }

    // Um provedor pode enviar ações independentes no mesmo lote. A evidência
    // deve acompanhar a entrega à qual o alvo pertence, em vez de contaminar a
    // etapa que estava selecionada quando o lote começou.
    const candidates = new Set<string>();
    for (const artifact of this.executionLedger(turnId)) {
      if (artifact.status === "live" && elementIds.includes(artifact.elementId)) candidates.add(artifact.stepId);
    }
    for (const resolution of plan?.designResolutions ?? []) {
      if (!resolution.targetReferenceElementId || !elementIds.includes(resolution.targetReferenceElementId)) continue;
      if (mutationSupportsDesignAttribute(capability, resolution.attribute)) candidates.add(resolution.stepId);
    }
    if (candidates.size === 1) return [...candidates][0];

    // Inputs relacionais podem referenciar outro móvel, mas o elementId é o
    // alvo real da mutação. Não use referenceElementId para atribuir a etapa.
    void input;
    return this.activeExecutionStepByTurn.get(turnId);
  }

  /**
   * Para cada alvo suspenso, o vizinho suspenso mais próximo na mesma parede —
   * a referência de linha que a elevação do conjunto precisa enquadrar.
   */
  private sameFamilyNeighbours(scene: SceneDocument, targetIds: ReadonlyArray<string>): string[] {
    if (!targetIds.length) return [];
    const reading = buildEnvironmentReading(scene, { focusElementIds: [...targetIds] });
    const neighbours: string[] = [];
    for (const targetId of targetIds) {
      const target = reading.elements.find((entry) => entry.elementId === targetId);
      if (!target?.suspended || !target.wallId) continue;
      const targetElement = scene.elements.find((entry) => entry.id === targetId);
      if (!targetElement) continue;
      const targetCenter = renderedElementCenterMm(targetElement);
      const candidates = reading.elements
        .filter((entry) => entry.elementId !== targetId && entry.suspended && entry.wallId === target.wallId && !targetIds.includes(entry.elementId))
        .map((entry) => {
          const element = scene.elements.find((candidate) => candidate.id === entry.elementId);
          const center = element ? renderedElementCenterMm(element) : null;
          const distance = center ? Math.hypot(center[0] - targetCenter[0], center[2] - targetCenter[2]) : Number.POSITIVE_INFINITY;
          return { id: entry.elementId, distance };
        })
        .sort((left, right) => left.distance - right.distance);
      if (candidates[0] && Number.isFinite(candidates[0].distance)) neighbours.push(candidates[0].id);
    }
    return [...new Set(neighbours)];
  }

  private recordReviewVerdict(turnId: string, entry: Omit<ReviewLedgerEntry, "round" | "stateThen">): void {
    const ledger = this.reviewLedgerByTurn.get(turnId) ?? [];
    const element = useEditorStore.getState().scene.elements.find((candidate) => candidate.id === entry.elementId);
    const band = element ? this.verticalBandOf(element) : null;
    ledger.push({
      ...entry,
      round: ledger.filter((previous) => previous.elementId === entry.elementId).length + 1,
      stateThen: band,
    });
    this.reviewLedgerByTurn.set(turnId, ledger.slice(-60));
  }

  private verticalBandOf(element: SceneElement): { bottomMm: number; topMm: number } {
    const scene = useEditorStore.getState().scene;
    const floor = checkElementGrounded(scene, element).floorElevationMm;
    const [bottom, top] = renderedElementVerticalIntervalMm(element);
    return { bottomMm: Math.round(bottom - floor), topMm: Math.round(top - floor) };
  }

  /**
   * The critic's previous verdicts on the given elements in this turn, with
   * the state they judged and the state now — so a blind critic still knows
   * what it asked for and what the executor did about it.
   */
  reviewHistoryFor(turnId: string, elementIds: ReadonlyArray<string>): Array<ReviewLedgerEntry & { stateNow: { bottomMm: number; topMm: number } | null }> {
    const ledger = this.reviewLedgerByTurn.get(turnId) ?? [];
    const scene = useEditorStore.getState().scene;
    const ids = new Set(elementIds);
    return ledger
      .filter((entry) => ids.has(entry.elementId))
      .slice(-12)
      .map((entry) => {
        const element = scene.elements.find((candidate) => candidate.id === entry.elementId);
        return { ...entry, stateNow: element ? this.verticalBandOf(element) : null };
      });
  }

  /**
   * Detects the see-saw: the element sits again (±25 mm) on a vertical band an
   * earlier verdict of this turn rejected, after having been moved away from
   * it in between. Returns a description, or null.
   */
  reviewOscillation(turnId: string, elementId: string): string | null {
    const ledger = (this.reviewLedgerByTurn.get(turnId) ?? []).filter((entry) => entry.elementId === elementId && entry.stateThen);
    if (ledger.length < 2) return null;
    const element = useEditorStore.getState().scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return null;
    const now = this.verticalBandOf(element);
    const same = (left: { bottomMm: number; topMm: number }, right: { bottomMm: number; topMm: number }) => (
      Math.abs(left.bottomMm - right.bottomMm) <= 25 && Math.abs(left.topMm - right.topMm) <= 25
    );
    const last = ledger[ledger.length - 1]!;
    if (last.stateThen && same(last.stateThen, now)) return null; // not moved since the last verdict: no swing
    const revisited = ledger.slice(0, -1).find((entry) => entry.verdict === "rejected" && entry.stateThen && same(entry.stateThen, now));
    if (!revisited) return null;
    return `o elemento voltou à cota ${now.bottomMm}–${now.topMm} mm que o laudo ${revisited.round} já havia reprovado (${revisited.failedAspects.join(", ") || "sem aspecto"}), depois de o laudo ${last.round} pedir outra cota (${last.stateThen?.bottomMm}–${last.stateThen?.topMm} mm)`;
  }

  /**
   * The declared datum/premise that protects the element's vertical cota from
   * being re-decided by the critic, or null when the plan declared none, the
   * built state does not honour it, or the environment gate objected.
   */
  declaredDatumProtection(turnId: string, elementId: string, builtStateValidated: boolean): string | null {
    if (!builtStateValidated) return null;
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan) return null;
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return null;
    const decision = plan.spatialDecisions.find((candidate) => (
      ["above", "below"].includes(candidate.relation)
      && candidate.datum
      && (candidate.bottomHeightMm != null || candidate.topHeightMm != null)
      && (candidate.subjectElementId === element.id || planSubjectsMatch(candidate.subject, element.name))
    ));
    if (!decision?.datum) return null;
    const datum = decision.datum;
    if (datum.kind === "use-band" || datum.kind === "reference-top") return null;
    const label = datum.kind === "new-line"
      ? `linha nova justificada: ${datum.justification ?? ""}`
      : `${datum.kind}${datum.reference ? ` · ${datum.reference}` : ""}`;
    const premise = (plan.assumptions ?? []).find((entry) => entry.topic === "reach");
    return premise ? `${label}; premissa: ${premise.assumption}` : label;
  }

  /** The structured material of the element when it matches the plan's decided material or reference appearance. */
  structuredMaterialProtection(turnId: string, elementId: string): string | null {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan) return null;
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return null;
    const resolutions = plan.designResolutions.filter((resolution) => (
      ["material", "appearance"].includes(resolution.attribute)
      && (resolution.subjectElementId === element.id || planSubjectsMatch(resolution.subject, element.name))
    ));
    for (const resolution of resolutions) {
      if (resolution.targetReferenceElementId) {
        const reference = scene.elements.find((candidate) => candidate.id === resolution.targetReferenceElementId);
        if (reference && reference.materialId === element.materialId
          && JSON.stringify(reference.materialAssignments) === JSON.stringify(element.materialAssignments)) {
          return `${element.materialId} igual a ${reference.name}`;
        }
      }
      if (resolution.targetMaterialCatalogItemId) {
        const target = this.catalogById.get(resolution.targetMaterialCatalogItemId)?.stableItemId ?? resolution.targetMaterialCatalogItemId;
        if (element.materialId === target) return element.materialId;
      }
    }
    return null;
  }

  /** Pontos embutidos cobertos pelo elemento: observação de detalhamento, não reprovação. */
  fixtureObservations(elementId: string): string[] {
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return [];
    return fixtureInterferenceObservations(element.name, checkElementInstallation(scene, element));
  }

  private independentReviewIssues(turnId: string, elementId: string): string[] {
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return ["O elemento alterado não existe mais na cena para a conferência independente."];
    const installation = checkElementInstallation(scene, element);
    const plan = this.taskPlanByTurn.get(turnId);
    const createdThisTurn = this.executionLedger(turnId).some((artifact) => artifact.status === "live" && artifact.elementId === elementId);
    return independentInstallationIssues(
      plan,
      element.name,
      installation,
      {
        ...installationReviewDescriptors(scene, element, installation.nearestElements.map((neighbor) => neighbor.elementId)),
        // Numbers dictated by the designer (strict brief) are not second-guessed
        // by the typology rule; a module the agent created or freely implanted is.
        enforceTypology: plan ? plan.designFreedom !== "strict" : createdThisTurn,
      },
    );
  }

  private designResolutionsForElement(turnId: string, elementId: string, elementName: string) {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan) return [];
    const stepIds = new Set<string>();
    for (const artifact of this.executionLedger(turnId)) {
      if (artifact.status === "live" && artifact.elementId === elementId) stepIds.add(artifact.stepId);
    }
    for (const mutation of this.stepMutationEvidenceByTurn.get(turnId) ?? []) {
      if (!mutation.elementIds.includes(elementId)) continue;
      const resolutions = plan.designResolutions.filter((resolution) => resolution.stepId === mutation.stepId);
      if (resolutions.some((resolution) => mutationSupportsDesignAttribute(mutation.capability, resolution.attribute))) {
        stepIds.add(mutation.stepId);
      }
    }
    const byBoundStep = plan.designResolutions.filter((resolution) => stepIds.has(resolution.stepId));
    if (byBoundStep.length) return byBoundStep;
    return plan.designResolutions.filter((resolution) => requiredAgentDesignReviewAspects({
      ...plan,
      designResolutions: [resolution],
    }, elementName).length > 0 || plan.decisionAuthorities.some((authority) => (
      authority.subject === resolution.subject && elementRequiresAgentDesignReview(plan, elementName)
    )));
  }

  private professionalQualityTargetIds(turnId: string, stepId: string): string[] {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan) return [];
    const resolutions = plan.designResolutions.filter((resolution) => (
      resolution.stepId === stepId && resolution.authority === "agent"
    ));
    if (!resolutions.length) return [];
    const scene = useEditorStore.getState().scene;
    const mutationIds = (this.stepMutationEvidenceByTurn.get(turnId) ?? [])
      .filter((mutation) => mutation.stepId === stepId && resolutions.some((resolution) => (
        mutationSupportsDesignAttribute(mutation.capability, resolution.attribute)
      )))
      .flatMap((mutation) => mutation.elementIds);
    const artifactIds = this.executionLedger(turnId)
      .filter((artifact) => artifact.status === "live" && artifact.stepId === stepId)
      .map((artifact) => artifact.elementId);
    return [...new Set([...mutationIds, ...artifactIds])]
      .filter((id) => Boolean(findSceneTargetName(scene, id)));
  }

  /**
   * O que a tipologia e o estado observado do alvo dizem ao crítico — a
   * rubrica por família (altura de uso, linhas da parede, operação) não
   * depende do que o planejador escreveu nos próprios critérios.
   */
  reviewTargetProfile(elementId: string): ReviewTargetProfile | null {
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return null;
    const reading = buildEnvironmentReading(scene, { focusElementIds: [elementId] });
    const entry = reading.elements.find((candidate) => candidate.elementId === elementId);
    if (!entry) return null;
    return { kind: entry.typology.kind, suspended: entry.suspended, ...(entry.openingKind ? { openingKind: entry.openingKind } : {}) };
  }

  private requiredDesignAspectsForElement(turnId: string, elementId: string, elementName: string) {
    const plan = this.taskPlanByTurn.get(turnId);
    if (!plan || plan.designFreedom === "strict") return [];
    const resolutions = this.designResolutionsForElement(turnId, elementId, elementName)
      .filter((resolution) => resolution.authority === "agent");
    const bound = resolutions
      .flatMap((resolution) => resolution.reviewCriteria.map((criterion) => criterion.aspect));
    const authoredAttributes = new Set(resolutions.map((resolution) => resolution.attribute));
    const generic = independentAgentDesignReviewAspects([...authoredAttributes], this.reviewTargetProfile(elementId));
    return [...new Set([...generic, ...requiredAgentDesignReviewAspects(plan, elementName), ...bound])];
  }

  /**
   * Régua da pessoa e da parede sobre o elemento como ficou. Bloqueios pedem
   * emenda do plano (nunca nudge); sem plano, viram observações do laudo.
   */
  independentUseReview(turnId: string, elementId: string): { issues: string[]; observations: string[] } {
    const scene = useEditorStore.getState().scene;
    const element = scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return { issues: [], observations: [] };
    const plan = this.taskPlanByTurn.get(turnId);
    const createdThisTurn = this.executionLedger(turnId).some((artifact) => artifact.status === "live" && artifact.elementId === elementId);
    const reading = buildEnvironmentReading(scene, { focusElementIds: [elementId] });
    return independentUseIssues({
      plan,
      elementName: element.name,
      reading,
      elementId,
      enforce: plan ? plan.designFreedom !== "strict" : createdThisTurn,
    });
  }

  private designAuthorityForElement(
    turnId: string,
    elementId: string,
    attribute: EnvironmentReviewIssue["attribute"],
  ): "explicit" | "agent" | null {
    const elementName = findSceneTargetName(useEditorStore.getState().scene, elementId) ?? elementId;
    const resolutions = this.designResolutionsForElement(turnId, elementId, elementName);
    const exact = resolutions.filter((resolution) => resolution.attribute === attribute);
    // Only an explicit resolution protects an attribute from the reviewer. A
    // derived value is the geometric realization of a directive; whether the
    // directive itself is protected is decided by decisionAuthorities, not by
    // the label the planner put on the number. Treating derived as explicit
    // silently discarded final-review objections about heights and gaps.
    if (exact.some((resolution) => resolution.authority === "explicit")) return "explicit";
    if (exact.some((resolution) => resolution.authority === "agent")) return "agent";
    if (resolutions.some((resolution) => (
      resolution.authority === "agent"
      && ["position", "dimensions", "orientation", "material", "product", "relation", "appearance"].includes(resolution.attribute)
    ))) return "agent";
    return null;
  }

  private async structuredFunctionReviewForElement(turnId: string, elementId: string) {
    const element = useEditorStore.getState().scene.elements.find((candidate) => candidate.id === elementId);
    if (!element) return null;
    const assetId = dynamicAssetIdFor(element);
    if (!assetId) return null;
    const graph = await loadDynamicGraph(assetId, fetchDynamicGraph);
    if (!graph) return null;
    return structuredFunctionReview(
      this.taskPlanByTurn.get(turnId),
      element.name,
      dynamicInteractions(graph, element),
    );
  }

  async flushAudit(turnId: string, projectId = useEditorStore.getState().projectId): Promise<void> {
    try {
      await this.checkpointAudit(turnId, projectId, true);
      this.auditByTurn.delete(turnId);
    } catch (error) {
      // Keep the in-memory records available for another flush instead of
      // silently erasing the only forensic trace of the turn.
      console.error("Não foi possível persistir a auditoria completa do meX.", error);
    } finally {
      this.viewportCapturesByTurn.delete(turnId);
      this.pendingVisualIdsByTurn.delete(turnId);
      this.mutatedElementIdsByTurn.delete(turnId);
      this.mutatedElementIdsByStepByTurn.delete(turnId);
      this.stepMutationEvidenceByTurn.delete(turnId);
      this.viewportEvidenceByTurn.delete(turnId);
      this.viewportCaptureAliasByTurn.delete(turnId);
      this.visualEpochByTurn.delete(turnId);
      this.approvedVisualIdsByTurn.delete(turnId);
      this.environmentApprovedTurns.delete(turnId);
      this.reviewLedgerByTurn.delete(turnId);
      this.taskPlanByTurn.delete(turnId);
      this.activeExecutionStepByTurn.delete(turnId);
      this.executionArtifactsByTurn.delete(turnId);
      this.correctionAllowedIdsByTurn.delete(turnId);
      this.auditSequenceByTurn.delete(turnId);
    }
  }

  /**
   * Persists a long-running turn while it is still executing. Captures remain
   * available for the visual gate; only records acknowledged by the API leave
   * the in-memory queue.
   */
  async checkpointAudit(
    turnId: string,
    projectId = useEditorStore.getState().projectId,
    force = false,
  ): Promise<void> {
    const records = [...(this.auditByTurn.get(turnId) ?? [])];
    if (!records.length || (!force && records.length < 8)) return;
    for (const batch of auditBatches(records)) {
      let sent = false;
      let lastError: unknown;
      for (let attempt = 0; attempt < 4 && !sent; attempt += 1) {
        try {
          await api("/ai/executions", {
            method: "POST",
            agentTurnId: turnId,
            body: { turnId, projectId, records: batch },
          });
          sent = true;
        } catch (error) {
          lastError = error;
          if (attempt < 3) {
            await new Promise((resolve) => window.setTimeout(resolve, 750 * 2 ** attempt));
          }
        }
      }
      if (!sent) throw lastError;
      const remaining = this.auditByTurn.get(turnId) ?? [];
      this.auditByTurn.set(turnId, remaining.slice(batch.length));
    }
  }

  private recordExecution(turnId: string, capability: string, input: unknown, output: unknown, durationMs: number, failed: boolean): void {
    const bucket = this.auditByTurn.get(turnId) ?? [];
    const sequence = this.auditSequenceByTurn.get(turnId) ?? 0;
    bucket.push({
      sequence,
      recordedAt: new Date().toISOString(),
      capability,
      arguments: summarizeForAudit(input),
      result: capability === "scene.viewport.capture" ? captureForAudit(output) : summarizeForAudit(output),
      durationMs: Math.round(durationMs),
      failed,
    });
    this.auditByTurn.set(turnId, bucket);
    this.auditSequenceByTurn.set(turnId, sequence + 1);
  }

  private async executeRest(handler: RestCapabilityHandler, input: RecordInput, turnId: string): Promise<unknown> {
    const consumed = new Set<string>();
    let path = handler.path.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_match, key: string) => {
      const value = input[key];
      if (typeof value !== "string" || !value) throw new Error(`Falta ${key} para chamar ${handler.path}.`);
      consumed.add(key);
      return encodeURIComponent(value);
    });
    const query = new URLSearchParams();
    for (const key of handler.query ?? []) {
      consumed.add(key);
      const value = input[key];
      if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
    }
    if (query.size) path += `?${query.toString()}`;
    let body = input.body;
    if (body && typeof body === "object" && !Array.isArray(body)) {
      body = { ...(body as RecordInput) };
      if (handler.startsJob && !(body as RecordInput).idempotencyKey) {
        (body as RecordInput).idempotencyKey = `agent-${turnId}-${Date.now().toString(36)}`;
      }
    }
    if (body === undefined && handler.method !== "GET") {
      body = Object.fromEntries(Object.entries(input).filter(([key]) => !consumed.has(key)));
    }
    return api(path, {
      method: handler.method,
      ...(body !== undefined && handler.method !== "GET" ? { body } : {}),
      agentTurnId: turnId,
    });
  }

  private async waitForJob(input: RecordInput, turnId: string): Promise<unknown> {
    const deadline = Date.now() + Number(input.maxWaitSeconds) * 1_000;
    let lastProgress = -1;
    while (Date.now() < deadline) {
      const response = await api<{ data: RecordInput }>(`/jobs/${encodeURIComponent(String(input.jobId))}`, { agentTurnId: turnId });
      const job = response.data;
      const progress = Number(job.progress ?? 0);
      if (progress !== lastProgress) {
        lastProgress = progress;
        this.progressHandler?.(`Processamento em ${Math.max(0, Math.min(100, progress))}%…`);
      }
      if (["completed", "failed", "cancelled"].includes(String(job.state))) {
        return { job, completed: job.state === "completed" };
      }
      await new Promise((resolve) => window.setTimeout(resolve, 750));
    }
    const response = await api<{ data: RecordInput }>(`/jobs/${encodeURIComponent(String(input.jobId))}`, { agentTurnId: turnId });
    return { job: response.data, completed: false };
  }

  private addModule(input: RecordInput, turnId: string): unknown {
    const item = this.requireCatalogItem(String(input.catalogItemId));
    if (item.type === "material") throw new Error(`“${item.name}” é material, não módulo.`);
    const instanceKey = normalizeCreationInstanceKey(input.instanceKey);
    const catalogItemId = item.stableItemId;
    const stepId = this.creationStepId(turnId, catalogItemId, instanceKey);
    const productResolutions = (this.taskPlanByTurn.get(turnId)?.designResolutions ?? [])
      .filter((resolution) => resolution.stepId === stepId
        && resolution.attribute === "product"
        && (!resolution.instanceKey || resolution.instanceKey === instanceKey)
        && resolution.targetCatalogItemId);
    if (productResolutions.length) {
      const allowed = new Set(productResolutions.map((resolution) => (
        this.catalogById.get(resolution.targetCatalogItemId!)?.stableItemId ?? resolution.targetCatalogItemId!
      )));
      if (!allowed.has(catalogItemId)) {
        throw new Error(`O plano já escolheu profissionalmente outro produto para esta unidade. Use o catalogItemId registrado em designResolutions; não implante o primeiro resultado da busca.`);
      }
    }
    const artifacts = this.executionLedger(turnId);
    const reusable = findReusableCreation(artifacts, {
      stepId,
      catalogItemId,
      instanceKey,
    }, new Set(useEditorStore.getState().scene.elements.map((element) => element.id)));
    if (reusable) {
      return {
        element: snapshot(requireElement(reusable.elementId)),
        stepId,
        instanceKey,
        reused: true,
        message: `A instância “${instanceKey}” desta entrega já existe como ${reusable.elementId}. Continue corrigindo e verificando esse elemento; não crie uma cópia.`,
      };
    }
    const createdId = withAgentTurn(turnId, () => useEditorStore.getState().addCatalogItem(
      item,
      input.positionMm as Vec3,
    ));
    if (!createdId) throw new Error(`Não foi possível inserir “${item.name}”.`);
    const element = requireElement(createdId);
    this.executionArtifactsByTurn.set(turnId, registerCreation(artifacts, {
      stepId,
      instanceKey,
      elementId: createdId,
      catalogItemId,
      name: element.name,
      status: "live",
      translationMm: [...element.transform.translationMm] as Vec3,
    }));
    return {
      element: snapshot(element),
      stepId,
      instanceKey,
      reused: false,
    };
  }

  private creationStepId(turnId: string, catalogItemId: string, instanceKey: string): string {
    const plan = this.taskPlanByTurn.get(turnId);
    const matchingSteps = new Set((plan?.designResolutions ?? [])
      .filter((resolution) => resolution.attribute === "product"
        && resolution.targetCatalogItemId
        && (!resolution.instanceKey || resolution.instanceKey === instanceKey)
        && (this.catalogById.get(resolution.targetCatalogItemId)?.stableItemId ?? resolution.targetCatalogItemId) === catalogItemId)
      .map((resolution) => resolution.stepId));
    if (matchingSteps.size === 1) return [...matchingSteps][0]!;
    return this.activeExecutionStepByTurn.get(turnId) ?? "direct";
  }

  private deleteElements(input: RecordInput, turnId: string): unknown {
    const ids = input.elementIds as string[];
    const deletedCreationIntents = this.executionLedger(turnId)
      .filter((artifact) => ids.includes(artifact.elementId))
      .map(({ stepId, instanceKey, catalogItemId }) => ({ stepId, instanceKey, catalogItemId }));
    const result = deleteElements(input, turnId) as { deletedIds: string[]; deletedNames: string[] };
    this.executionArtifactsByTurn.set(turnId, markCreationsDeleted(
      this.executionArtifactsByTurn.get(turnId) ?? [],
      new Set(result.deletedIds),
    ));
    return {
      ...result,
      ...(deletedCreationIntents.length ? { deletedCreationIntents } : {}),
    };
  }

  private async installModule(input: RecordInput, turnId: string): Promise<unknown> {
    const referenceId = String(input.referenceElementId);
    const scene = useEditorStore.getState().scene;
    if (!scene.elements.some((element) => element.id === referenceId)) {
      if (scene.walls.some((wall) => wall.id === referenceId)) {
        return this.installModuleAgainstArchitecturalWall(input, turnId);
      }
      if (scene.rooms.some((room) => room.id === referenceId)) {
        throw new Error(`O ambiente ${referenceId} não define sozinho uma face de implantação. Use em referenceElementId uma parede real lida da cena e, para um canto, informe a segunda parede em towardsWallId.`);
      }
    }
    const reference = requireElement(referenceId);
    const initialCenter = renderedElementCenterMm(reference);
    const added = this.addModule({
      catalogItemId: input.catalogItemId,
      instanceKey: input.instanceKey,
      positionMm: initialCenter,
    }, turnId) as Record<string, unknown>;
    const addedElement = added.element as Record<string, unknown>;
    const elementId = String(addedElement.id);
    await this.awaitCurrentRenderedGeometry(elementId);

    let resized = false;
    if (input.dimensionsMm) {
      const result = await this.resizeElement({ elementId, dimensionsMm: input.dimensionsMm }, turnId) as Record<string, unknown>;
      resized = result.unchanged !== true;
      await this.awaitCurrentRenderedGeometry(elementId);
    }

    const placed = this.placeElementByRelation({
      elementId,
      referenceElementId: input.referenceElementId,
      relation: input.relation,
      preferredSide: input.preferredSide,
      alignment: input.alignment,
      support: input.support,
      clearanceMm: input.clearanceMm,
      ...(typeof input.bottomHeightMm === "number" ? { bottomHeightMm: input.bottomHeightMm } : {}),
      wallId: input.wallId,
      roomId: input.roomId,
    }, turnId) as Record<string, unknown>;
    await this.awaitCurrentRenderedGeometry(elementId);

    let appearanceMatched = false;
    let appearanceChanged = false;
    if (typeof input.appearanceReferenceElementId === "string") {
      const matched = matchElementAppearance({
        elementId,
        referenceElementId: input.appearanceReferenceElementId,
      }, turnId) as Record<string, unknown>;
      appearanceMatched = true;
      appearanceChanged = matched.unchanged !== true;
    }
    // A module that arrives with a leaf open (the SKP default pose of some
    // assets) contaminates every review that follows: the critic reads an
    // open flap as a defect and the executor spends a round closing it. A
    // freshly installed module is delivered closed; the brief may open it later.
    const closedLeaves = Boolean(added.reused) ? [] : await this.closeOpenInteractions(elementId, turnId);
    const current = requireElement(elementId);
    return {
      element: snapshot(current),
      placement: placed.placement,
      stepId: String(added.stepId),
      instanceKey: String(added.instanceKey),
      reused: Boolean(added.reused),
      resized,
      appearanceMatched,
      changed: !Boolean(added.reused) || resized || placed.unchanged !== true || appearanceChanged || closedLeaves.length > 0,
      ...(closedLeaves.length ? { closedInteractionPaths: closedLeaves } : {}),
      ...(placed.typologyGuidance ? { typologyGuidance: placed.typologyGuidance } : {}),
    };
  }

  /**
   * `scene.module.install` historically accepted only another piece of
   * furniture as its reference. A room's walls are not SceneElements, so an
   * otherwise correct plan for an empty room could only loop on "wall not
   * found". Keep the same atomic capability and execution ledger, but resolve
   * real wall ids through the architectural envelope when support is floor.
   */
  private async installModuleAgainstArchitecturalWall(input: RecordInput, turnId: string): Promise<unknown> {
    if (input.relation !== "against") {
      throw new Error("Uma parede arquitetônica aceita relation=against. Para relações com outro móvel, use o elementId real desse móvel.");
    }
    if (input.support !== "floor" && input.support !== "auto") {
      throw new Error("A implantação arquitetônica por esta capacidade é para módulo apoiado no piso; elementos fixados na parede usam scene.element.mount.");
    }
    const clearance = input.clearanceMm as { minimum: number; target: number; maximum: number };
    const primaryWallId = String(input.referenceElementId);
    const secondaryWallId = typeof input.towardsWallId === "string" && input.towardsWallId !== primaryWallId
      ? input.towardsWallId
      : null;
    const roomId = typeof input.roomId === "string" ? input.roomId : null;
    const item = this.requireCatalogItem(String(input.catalogItemId));
    const preview = previewElementFromCatalog(item);
    // Validate every architectural reference before creating anything.
    const initialPlacement = resolveArchitecturalFloorPlacement({
      scene: useEditorStore.getState().scene,
      primaryWallId,
      secondaryWallId,
      roomId,
      sizeMm: preview.dimensionsMm,
      clearanceMm: clearance.target,
    });
    const added = this.addModule({
      catalogItemId: input.catalogItemId,
      instanceKey: input.instanceKey,
      positionMm: initialPlacement.physicalCenterMm,
    }, turnId) as Record<string, unknown>;
    const addedElement = added.element as Record<string, unknown>;
    const elementId = String(addedElement.id);
    await this.awaitCurrentRenderedGeometry(elementId);

    let resized = false;
    if (input.dimensionsMm) {
      const result = await this.resizeElement({ elementId, dimensionsMm: input.dimensionsMm }, turnId) as Record<string, unknown>;
      resized = result.unchanged !== true;
      await this.awaitCurrentRenderedGeometry(elementId);
    }

    const renderedSize = renderedElementSizeMm(requireElement(elementId));
    const placement = resolveArchitecturalFloorPlacement({
      scene: useEditorStore.getState().scene,
      primaryWallId,
      secondaryWallId,
      roomId,
      sizeMm: { width: renderedSize[0], height: renderedSize[1], depth: renderedSize[2] },
      clearanceMm: clearance.target,
    });
    const rotated = rotateElement({ elementId, rotationDeg: placement.rotationDeg }, turnId) as Record<string, unknown>;
    await this.awaitCurrentRenderedGeometry(elementId);
    const moved = moveElement({ elementId, physicalCenterMm: placement.physicalCenterMm }, turnId) as Record<string, unknown>;
    await this.awaitCurrentRenderedGeometry(elementId);

    let appearanceMatched = false;
    let appearanceChanged = false;
    if (typeof input.appearanceReferenceElementId === "string") {
      const matched = matchElementAppearance({
        elementId,
        referenceElementId: input.appearanceReferenceElementId,
      }, turnId) as Record<string, unknown>;
      appearanceMatched = true;
      appearanceChanged = matched.unchanged !== true;
    }
    const closedLeaves = Boolean(added.reused) ? [] : await this.closeOpenInteractions(elementId, turnId);
    const current = requireElement(elementId);
    const changed = !Boolean(added.reused)
      || resized
      || rotated.unchanged !== true
      || moved.unchanged !== true
      || appearanceChanged
      || closedLeaves.length > 0;
    return {
      element: snapshot(current),
      placement: {
        candidateId: `architectural:${placement.primaryWallId}:${placement.secondaryWallId ?? "center"}`,
        label: placement.secondaryWallId
          ? `Canto ${placement.primaryWallId}/${placement.secondaryWallId}`
          : `Contra ${placement.primaryWallId}`,
        positionMm: placement.physicalCenterMm,
        rotationDeg: placement.rotationDeg,
        side: "center",
        alignment: "back",
        wallId: placement.primaryWallId,
        geometryScore: 1,
        designScore: 1,
        totalScore: 1,
        valid: true,
        reasons: placement.reasons,
        clearance: {
          requestedMm: clearance,
          primaryWallId: placement.primaryWallId,
          secondaryWallId: placement.secondaryWallId,
        },
        grounded: { support: "floor", bottomMm: 0 },
        installation: { roomId: placement.roomId, support: "floor", architecturalReference: true },
      },
      stepId: String(added.stepId),
      instanceKey: String(added.instanceKey),
      reused: Boolean(added.reused),
      resized,
      appearanceMatched,
      changed,
      ...(closedLeaves.length ? { closedInteractionPaths: closedLeaves } : {}),
    };
  }

  /** Closes every dynamic interaction that is open on a module; returns the paths it closed. */
  private async closeOpenInteractions(elementId: string, turnId: string): Promise<string[]> {
    const element = requireElement(elementId);
    const assetId = dynamicAssetIdFor(element);
    if (!assetId) return [];
    const graph = await loadDynamicGraph(assetId, fetchDynamicGraph).catch(() => null);
    if (!graph) return [];
    const open = dynamicInteractions(graph, element).filter((interaction) => interaction.state === "open");
    if (!open.length) return [];
    const closed: string[] = [];
    for (const interaction of open) {
      try {
        const result = await this.interactPart({ elementId, path: interaction.path, desiredState: "closed" }, turnId) as Record<string, unknown>;
        const outcome = result.interaction as Record<string, unknown> | undefined;
        if (outcome?.changed) closed.push(interaction.path);
      } catch {
        // A leaf that cannot be closed is left for the model to handle explicitly.
      }
    }
    if (closed.length) await this.awaitCurrentRenderedGeometry(elementId);
    return closed;
  }

  private evaluatePlacementCandidates(input: RecordInput): unknown {
    const scene = useEditorStore.getState().scene;
    const source = typeof input.elementId === "string"
      ? requireElement(input.elementId, scene)
      : previewElementFromCatalog(this.requireCatalogItem(String(input.catalogItemId)));
    const candidates = (input.candidates as RecordInput[]).map((candidate) => {
      const rotationDeg = candidate.rotationDeg as Vec3;
      const rotationRad = rotationDeg.map((value) => value * Math.PI / 180) as Vec3;
      let preview: SceneElement = {
        ...source,
        transform: { ...source.transform, translationMm: candidate.positionMm as Vec3, rotationRad },
        extensions: { ...source.extensions },
      };
      if (candidate.support === "wall") {
        const wall = scene.walls.find((item) => item.id === candidate.wallId);
        if (!wall) throw new Error(`Parede ${String(candidate.wallId)} não foi encontrada.`);
        const mount = resolveWallMountSurface(scene, wall, candidate.positionMm as Vec3, {
          face: "interior",
          roomId: typeof candidate.roomId === "string" ? candidate.roomId : undefined,
        });
        const placement = resolveSurfacePlacement(
          scene,
          candidate.positionMm as Vec3,
          mount.surface,
          renderedElementSizeMm(preview),
          rotationRad,
          { snap: false },
        );
        const rootedPreview = semanticPreviewAtPhysicalCenter(preview, placement.positionMm, placement.rotationRad[1]);
        preview = {
          ...rootedPreview,
          transform: { ...rootedPreview.transform, rotationRad: placement.rotationRad },
          extensions: withElementPlacementSurface(rootedPreview, mount.surface),
        };
      }
      const clearance = checkElementClearance(scene, preview);
      const grounded = checkElementGrounded(scene, preview);
      const installation = checkElementInstallation(scene, preview);
      const supported = installation.support.status === "supported";
      const valid = clearance.collisions.length === 0 && supported;
      const geometryScore = Math.max(0, 100 - clearance.collisions.length * 35 - (supported ? 0 : 45));
      return {
        label: String(candidate.label),
        positionMm: preview.transform.translationMm,
        rotationDeg: preview.transform.rotationRad.map((value) => Math.round(value * 180 / Math.PI * 10) / 10) as Vec3,
        geometryScore,
        valid,
        clearance,
        grounded,
        installation,
      };
    });
    return { candidates };
  }

  private resolveSemanticPlacement(input: RecordInput, turnId: string): unknown {
    const resolution = this.semanticPlacementCandidates(input, turnId);
    return {
      recommended: resolution.recommended?.result ?? null,
      candidates: resolution.candidates.map((candidate) => candidate.result),
      blockedReasons: resolution.blockedReasons,
      typologyGuidance: resolution.typologyGuidance,
    };
  }

  private placeElementByRelation(input: RecordInput, turnId: string): unknown {
    const element = requireElement(String(input.elementId));
    const previousTransform = {
      translationMm: [...element.transform.translationMm] as Vec3,
      rotationRad: [...element.transform.rotationRad] as Vec3,
    };
    const resolution = this.semanticPlacementCandidates(input, turnId);
    const recommended = resolution.recommended;
    if (!recommended) {
      const reason = resolution.blockedReasons.join(" ") || "Nenhuma implantação geometricamente válida foi encontrada.";
      throw new Error(`${reason} O elemento existente ${element.id} permanece na cena e deve ser ajustado, relido ou substituído deliberadamente; não insira outra cópia para tentar a mesma implantação. Revise somente uma restrição material ou tente outra referência espacial.`);
    }
    const unchanged = sameTransformAndSurface(element, recommended.preview);
    if (unchanged) {
      return {
        element: snapshot(element),
        placement: recommended.result,
        alternativesEvaluated: resolution.candidates.length,
        previousTransform,
        unchanged: true,
        typologyGuidance: resolution.typologyGuidance,
      };
    }
    selectExactly([element.id]);
    withAgentTurn(turnId, () => useEditorStore.getState().updateSelected({
      transform: {
        ...element.transform,
        translationMm: recommended.preview.transform.translationMm,
        rotationRad: recommended.preview.transform.rotationRad,
      },
      extensions: recommended.preview.extensions,
    }));
    return {
      element: snapshot(requireElement(element.id)),
      placement: recommended.result,
      alternativesEvaluated: resolution.candidates.length,
      previousTransform,
      unchanged: false,
      typologyGuidance: resolution.typologyGuidance,
    };
  }

  private semanticPlacementCandidates(input: RecordInput, turnId?: string): SemanticPlacementResolution {
    const scene = useEditorStore.getState().scene;
    let source: SceneElement;
    if (typeof input.elementId === "string") {
      source = requireElement(input.elementId, scene);
    } else {
      const requestedCatalogId = String(input.catalogItemId);
      const stableCatalogId = this.requireCatalogItem(requestedCatalogId).stableItemId;
      const activeStepId = turnId ? this.activeExecutionStepByTurn.get(turnId) : undefined;
      const liveArtifacts = turnId
        ? this.executionLedger(turnId).filter((artifact) => artifact.status === "live"
          && artifact.catalogItemId === stableCatalogId
          && (!activeStepId || artifact.stepId === activeStepId))
        : [];
      const liveElements = liveArtifacts
        .map((artifact) => scene.elements.find((element) => element.id === artifact.elementId))
        .filter((element): element is SceneElement => Boolean(element));
      // A catalog preview is only valid before insertion. Once this delivery
      // owns a live artifact, evaluate that exact instance so it cannot collide
      // with itself or invite another duplicate.
      source = liveElements.length === 1
        ? requireElement(liveElements[0]!.id, scene)
        : previewElementFromCatalog(this.requireCatalogItem(requestedCatalogId));
    }
    const reference = requireElement(String(input.referenceElementId), scene);
    return buildSemanticPlacementCandidates(scene, source, reference, input);
  }

  private async resizeElement(input: RecordInput, turnId: string): Promise<unknown> {
    const element = requireElement(String(input.elementId));
    const item = element.catalogRef
      ? this.catalogById.get(element.catalogRef.itemId) ?? null
      : null;
    const dynamicAssetId = dynamicAssetIdFor(element);
    if (dynamicAssetId) {
      const graph = await loadDynamicGraph(dynamicAssetId, fetchDynamicGraph);
      if (!graph) throw new Error(`Não foi possível carregar as regras dinâmicas de “${element.name}”.`);
    }
    const requested = input.dimensionsMm as Partial<SceneElement["dimensionsMm"]>;
    const unchanged = (Object.keys(requested) as Array<keyof SceneElement["dimensionsMm"]>)
      .every((dimension) => requested[dimension] === undefined
        || Math.abs(element.dimensionsMm[dimension] - requested[dimension]!) <= 0.01);
    if (unchanged) {
      return {
        element: snapshot(element),
        requestedDimensionsMm: requested,
        unchanged: true,
      };
    }
    selectExactly([element.id]);
    withAgentTurn(turnId, () => useEditorStore.getState().resizeSelected(
      item,
      input.dimensionsMm as Partial<SceneElement["dimensionsMm"]>,
    ));
    const state = useEditorStore.getState();
    if (state.error) throw new Error(state.error);
    return {
      element: snapshot(requireElement(element.id, state.scene)),
      requestedDimensionsMm: input.dimensionsMm,
      unchanged: false,
    };
  }

  private paintElements(input: RecordInput, turnId: string): unknown {
    const material = this.requireCatalogItem(String(input.materialCatalogItemId));
    if (material.type !== "material") throw new Error(`“${material.name}” não é um material do catálogo.`);
    const ids = input.elementIds as string[];
    for (const id of ids) requireSceneTarget(id);
    withAgentTurn(turnId, () => useEditorStore.getState().paintPart(
      ids,
      input.path as string | null,
      { source: "catalog", item: material },
    ));
    const scene = useEditorStore.getState().scene;
    return {
      targets: ids.map((id) => targetSnapshot(scene, id)),
      materialName: material.name,
    };
  }

  private async searchCatalog(input: RecordInput): Promise<unknown> {
    const query = new URLSearchParams({
      query: String(input.query),
      limit: String(input.limit),
      catalogSchema: "engineering-v2",
    });
    if (input.type === "module" || input.type === "material") query.set("catalogKind", String(input.type));
    else if (input.type) query.set("type", String(input.type));
    const response = await api<{ data: CatalogItem[] }>(`/catalog-items?${query.toString()}`);
    this.updateCatalog(response.data);
    registerDynamicCatalog(response.data);
    return { items: response.data.map(catalogItemForAgent) };
  }

  private async readElement(input: RecordInput): Promise<unknown> {
    const element = findElement(String(input.elementId));
    const assetId = dynamicAssetIdFor(element);
    if (!assetId) return { element: snapshot(element) };
    const graph = await loadDynamicGraph(assetId, fetchDynamicGraph);
    if (!graph) return { element: snapshot(element) };
    return {
      element: snapshot(element),
      dynamic: { interactions: dynamicInteractions(graph, element) },
    };
  }

  private async interactPart(input: RecordInput, turnId: string): Promise<unknown> {
    const element = requireElement(String(input.elementId));
    const assetId = dynamicAssetIdFor(element);
    if (!assetId) throw new Error(`“${element.name}” não possui interações dinâmicas.`);
    const graph = await loadDynamicGraph(assetId, fetchDynamicGraph);
    if (!graph) throw new Error(`Não foi possível carregar as interações de “${element.name}”.`);
    const path = String(input.path);
    const context = dynamicInteractionContext(graph, element, path);
    if (!context) {
      throw new Error(`A interação ${path} não existe em “${element.name}”. Releia o elemento e use um path devolvido.`);
    }
    const desiredState = String(input.desiredState ?? "toggle") as InteractionDesiredState;
    const plan = planInteraction(context.script, context.current, desiredState, context.resting);
    if (!plan.changes.length && !plan.alreadySatisfied) {
      throw new Error(desiredState === "toggle"
        ? `A interação ${path} não declarou nenhuma mudança compatível.`
        : `Não foi possível identificar com segurança o estado ${desiredState === "closed" ? "fechado" : "aberto"} de ${path}.`);
    }

    withAgentTurn(turnId, () => {
      for (const change of plan.changes) {
        const target = context.resolve(change.node);
        if (target) useEditorStore.getState().setPartAttribute(element.id, target.path, change.attribute, change.value, "action");
      }
    });
    const updated = requireElement(element.id);
    const stateAfter = interactionState(graph, updated, path);
    const allInteractions = dynamicInteractions(graph, updated);
    return {
      element: snapshot(updated),
      interaction: {
        path,
        name: context.holder.name,
        desiredState,
        stateBefore: plan.stateBefore,
        stateAfter,
        changed: plan.changes.length > 0,
        alreadySatisfied: plan.alreadySatisfied,
        changedAttributes: plan.changes.map((change) => change.attribute),
      },
      allInteractions,
      allInteractionsClosed: allInteractions.length > 0
        && allInteractions.every((interaction) => interaction.state === "closed"),
      unchanged: plan.changes.length === 0,
    };
  }

  private async setPartGroupState(input: RecordInput, turnId: string): Promise<unknown> {
    const element = requireElement(String(input.elementId));
    const assetId = dynamicAssetIdFor(element);
    if (!assetId) throw new Error(`“${element.name}” não possui interações dinâmicas.`);
    const graph = await loadDynamicGraph(assetId, fetchDynamicGraph);
    if (!graph) throw new Error(`Não foi possível carregar as interações de “${element.name}”.`);
    const selector = String(input.selector ?? "all");
    const nameNeedle = typeof input.nameContains === "string"
      ? normalizeLookupText(input.nameContains)
      : null;
    const interactions = dynamicInteractions(graph, element);
    const matched = interactions.filter((interaction) => {
      const searchable = normalizeLookupText(`${interaction.name} ${interaction.path}`);
      if (nameNeedle && !searchable.includes(nameNeedle)) return false;
      if (selector === "doors") return /\b(?:porta|portas|door|doors)\b/.test(searchable);
      if (selector === "drawers") return /\b(?:gaveta|gavetas|drawer|drawers)\b/.test(searchable);
      return true;
    });
    if (!matched.length) {
      throw new Error(`Nenhuma parte móvel de “${element.name}” corresponde ao seletor ${selector}${nameNeedle ? ` e ao nome “${input.nameContains}”` : ""}. Releia as interações; não use path vazio.`);
    }
    const changedPaths: string[] = [];
    const alreadySatisfiedPaths: string[] = [];
    for (const interaction of matched) {
      const result = await this.interactPart({
        elementId: element.id,
        path: interaction.path,
        desiredState: input.desiredState,
      }, turnId) as Record<string, unknown>;
      const outcome = result.interaction as Record<string, unknown>;
      if (outcome.changed) changedPaths.push(interaction.path);
      else alreadySatisfiedPaths.push(interaction.path);
    }
    const updated = requireElement(element.id);
    const updatedInteractions = dynamicInteractions(graph, updated);
    const desiredState = String(input.desiredState);
    return {
      element: snapshot(updated),
      matchedPaths: matched.map((interaction) => interaction.path),
      changedPaths,
      alreadySatisfiedPaths,
      allMatchedSatisfied: matched.every((interaction) => (
        updatedInteractions.find((candidate) => candidate.path === interaction.path)?.state === desiredState
      )),
      interactions: updatedInteractions,
      changed: changedPaths.length > 0,
      unchanged: changedPaths.length === 0,
    };
  }

  private requireCatalogItem(id: string): CatalogItem {
    const item = this.catalogById.get(id);
    if (!item) {
      const loaded = [...this.catalogById.values()]
        .slice(-8)
        .map((candidate) => `${candidate.name}=${candidate.stableItemId}`);
      throw new Error(`Item de catálogo ${id} não está carregado. Use literalmente um ID retornado pela busca${loaded.length ? `; resultados carregados recentemente: ${loaded.join("; ")}` : ""}. Não fabrique um alias nem repita a busca se o item correto já estiver nessa lista.`);
    }
    return item;
  }
}

function isVisualSceneMutation(definition: CapabilityDefinition): boolean {
  return definition.plane === "client"
    && definition.effect !== "read"
    && definition.name.startsWith("scene.")
    && !["scene.selection.set", "scene.camera.set", "scene.view.mode.set", "scene.undo", "scene.redo", "scene.save"].includes(definition.name);
}

function mutationResultChanged(output: unknown): boolean {
  if (!output || typeof output !== "object" || Array.isArray(output)) return true;
  const record = output as RecordInput;
  if (typeof record.changed === "boolean") return record.changed;
  return !(record.reused === true || record.unchanged === true);
}

/**
 * Resolves the provider's function-call id to the capture id returned by the
 * viewport tool. Some Responses-compatible providers echo the former in a
 * subsequent review even though the capability contract expects the latter.
 * Only captures from the current visual epoch are supplied by the caller, so
 * this repair can never revive evidence taken before a scene mutation.
 */
export function resolveViewportCaptureReference(
  requestedId: string,
  currentCaptureIds: readonly string[],
  aliases: ReadonlyMap<string, string> | undefined,
): string | null {
  if (currentCaptureIds.includes(requestedId)) return requestedId;
  const aliased = aliases?.get(requestedId);
  if (aliased && currentCaptureIds.includes(aliased)) return aliased;
  return null;
}

function executionElementIds(input: RecordInput, output: unknown): string[] {
  const ids = new Set<string>();
  for (const key of ["elementId", "openingId"] as const) {
    if (typeof input[key] === "string") ids.add(input[key] as string);
  }
  if (Array.isArray(input.elementIds)) input.elementIds.forEach((id) => typeof id === "string" && ids.add(id));
  if (output && typeof output === "object" && !Array.isArray(output)) {
    const record = output as RecordInput;
    const element = record.element;
    if (element && typeof element === "object" && typeof (element as RecordInput).id === "string") {
      ids.add(String((element as RecordInput).id));
    }
    if (Array.isArray(record.elements)) {
      record.elements.forEach((candidate) => {
        if (candidate && typeof candidate === "object" && typeof (candidate as RecordInput).id === "string") {
          ids.add(String((candidate as RecordInput).id));
        }
      });
    }
    for (const key of ["wall", "opening", "room"] as const) {
      const target = record[key];
      if (target && typeof target === "object" && typeof (target as RecordInput).id === "string") {
        ids.add(String((target as RecordInput).id));
      }
    }
  }
  return [...ids];
}

export function executionTargetIds(name: string, input: RecordInput): string[] {
  const ids = new Set<string>();
  if (typeof input.elementId === "string") ids.add(String(input.elementId));
  if (Array.isArray(input.elementIds)) {
    input.elementIds.forEach((id) => typeof id === "string" && ids.add(id));
  }
  // Ids de referência (wallId de fixação, referenceElementId, roomId) não são
  // alvos de mutação: só contam como alvo nas capacidades cujo objeto é a
  // própria parede/abertura/medida/camada.
  if (name.startsWith("scene.wall.") && typeof input.wallId === "string") ids.add(String(input.wallId));
  if (name.startsWith("scene.opening.") && typeof input.openingId === "string") ids.add(String(input.openingId));
  if (name.startsWith("scene.measurement.") && typeof input.measurementId === "string") ids.add(String(input.measurementId));
  if (name.startsWith("scene.layer.") && typeof input.layerId === "string") ids.add(String(input.layerId));
  if (name.includes("operation") && typeof input.operationId === "string") ids.add(String(input.operationId));
  return [...ids];
}

/** Menor distância horizontal entre os envelopes em planta de dois elementos (0 quando se sobrepõem). */
function horizontalGapBetweenElementsMm(left: SceneElement, right: SceneElement): number {
  const footprint = (element: SceneElement) => {
    const center = renderedElementCenterMm(element);
    const size = renderedElementSizeMm(element);
    const yaw = element.transform.rotationRad[1] ?? 0;
    const extentX = (Math.abs(size[0] * Math.cos(yaw)) + Math.abs(size[2] * Math.sin(yaw))) / 2;
    const extentZ = (Math.abs(size[0] * Math.sin(yaw)) + Math.abs(size[2] * Math.cos(yaw))) / 2;
    return { minX: center[0] - extentX, maxX: center[0] + extentX, minZ: center[2] - extentZ, maxZ: center[2] + extentZ };
  };
  const a = footprint(left);
  const b = footprint(right);
  const gapX = Math.max(a.minX - b.maxX, b.minX - a.maxX, 0);
  const gapZ = Math.max(a.minZ - b.maxZ, b.minZ - a.maxZ, 0);
  return Math.hypot(gapX, gapZ);
}

function dynamicInteractions(graph: DynamicComponentGraph, element: SceneElement): Array<{ path: string; name: string; state: InteractionState }> {
  const interactions: Array<{ path: string; name: string; state: InteractionState }> = [];
  const visit = (node: DynamicNode): void => {
    if (typeof node.attributes.onclick?.value === "string" && node.attributes.onclick.value.trim()) {
      interactions.push({ path: node.path, name: node.name, state: interactionState(graph, element, node.path) });
    }
    node.children.forEach(visit);
  };
  visit(graph.root);
  return interactions;
}

function interactionState(graph: DynamicComponentGraph, element: SceneElement, path: string): InteractionState {
  const context = dynamicInteractionContext(graph, element, path);
  return context ? planInteraction(context.script, context.current, "toggle", context.resting).stateBefore : "unknown";
}

function dynamicInteractionContext(graph: DynamicComponentGraph, element: SceneElement, path: string): {
  holder: DynamicNode;
  script: string;
  resolve(nodeName: string): DynamicNode | undefined;
  current(nodeName: string, attribute: string): string | number | null;
  resting(nodeName: string, attribute: string): string | number | null;
} | null {
  const chain = dynamicAncestors(graph.root, path);
  const holder = chain.at(-1);
  const script = holder?.attributes.onclick?.value;
  if (!holder || typeof script !== "string" || !script.trim()) return null;
  const overrides = readAttributeOverrides(element);
  const evaluation = evaluateDynamicFor(graph, element.dimensionsMm, overrides);
  const byName = new Map(chain.map((node) => [node.name.toLocaleLowerCase("pt-BR"), node]));
  const above = chain.at(-2);
  const resolve = (nodeName: string): DynamicNode | undefined => {
    const normalized = nodeName.toLocaleLowerCase("pt-BR");
    if (!normalized) return holder;
    if (normalized === "parent" || normalized === "owner") return above;
    return byName.get(normalized) ?? findDynamicNodeByName(graph.root, normalized) ?? undefined;
  };
  const current = (nodeName: string, attribute: string): string | number | null => {
    const target = resolve(nodeName);
    if (!target) return null;
    const stored = overrides[overrideKey(target.path, attribute)];
    if (stored !== undefined) return stored;
    const resolved = evaluation.parts.find((part) => part.path === target.path)?.attributes?.[attribute];
    if (typeof resolved === "string" || typeof resolved === "number") return resolved;
    const declared = target.attributes[attribute]?.value;
    return typeof declared === "string" || typeof declared === "number" ? declared : null;
  };
  const resting = (nodeName: string, attribute: string): string | number | null => {
    const target = resolve(nodeName);
    if (!target) return null;
    const declared = target.attributes[attribute]?.value;
    return typeof declared === "string" || typeof declared === "number" ? declared : null;
  };
  return { holder, script, resolve, current, resting };
}

function dynamicAncestors(root: DynamicNode, path: string): DynamicNode[] {
  const chain: DynamicNode[] = [];
  const visit = (node: DynamicNode): boolean => {
    if (node.path === path) {
      chain.push(node);
      return true;
    }
    for (const child of node.children) {
      if (!visit(child)) continue;
      chain.unshift(node);
      return true;
    }
    return false;
  };
  visit(root);
  return chain;
}

function findDynamicNodeByName(root: DynamicNode, name: string): DynamicNode | null {
  if (root.name.toLocaleLowerCase("pt-BR") === name) return root;
  for (const child of root.children) {
    const found = findDynamicNodeByName(child, name);
    if (found) return found;
  }
  return null;
}

function duplicateElements(input: RecordInput, turnId: string): unknown {
  const ids = input.elementIds as string[];
  ids.forEach((id) => requireElement(id));
  const before = new Set(useEditorStore.getState().scene.elements.map((element) => element.id));
  selectExactly(ids);
  withAgentTurn(turnId, () => useEditorStore.getState().duplicateSelection());
  const elements = useEditorStore.getState().scene.elements
    .filter((element) => !before.has(element.id))
    .map(snapshot);
  return { elements };
}

interface SemanticPlacementCandidateResult {
  candidateId: string;
  label: string;
  positionMm: Vec3;
  rotationDeg: Vec3;
  side: "left" | "right" | "front" | "back" | "center";
  alignment: "front" | "back" | "center";
  wallId: string | null;
  geometryScore: number;
  designScore: number;
  totalScore: number;
  valid: boolean;
  reasons: string[];
  clearance: Record<string, unknown>;
  grounded: Record<string, unknown>;
  installation: Record<string, unknown>;
}

interface SemanticPlacementCandidateInternal {
  result: SemanticPlacementCandidateResult;
  preview: SceneElement;
}

export interface SemanticPlacementTypologyGuidance {
  targetTypology: TypologyGuidance["targetTypology"];
  referenceTypology: TypologyGuidance["referenceTypology"];
  rule: TypologyGuidance["rule"];
  guidance: string;
  effectiveClearanceMm: { minimum: number; target: number; maximum: number } | null;
  bottomHeightMm: number | null;
  warnings: string[];
  activity: { activity: string; label: string; confidence: string; basis: string } | null;
  band: { bottomHeightMm: [number, number] | null; clearanceMm: [number, number] | null; lineMayOverride: boolean; basis: string[] } | null;
  wallLines: Array<{ heightMm: number; kind: string; family: string; sourceName?: string }>;
  heightCandidates: HeightCandidate[];
  reach: string | null;
  operation: string | null;
}

interface SemanticPlacementResolution {
  recommended: SemanticPlacementCandidateInternal | null;
  candidates: SemanticPlacementCandidateInternal[];
  blockedReasons: string[];
  typologyGuidance: SemanticPlacementTypologyGuidance;
}

function buildSemanticPlacementCandidates(
  scene: SceneDocument,
  source: SceneElement,
  reference: SceneElement,
  input: RecordInput,
): SemanticPlacementResolution {
  const relation = String(input.relation) as "beside" | "above" | "below" | "in_front_of" | "behind" | "stacked" | "against";
  const preferredSide = String(input.preferredSide ?? "auto") as "auto" | "left" | "right" | "front" | "back";
  const requestedAlignment = String(input.alignment ?? "auto") as "auto" | "front" | "back" | "center";
  const requestedSupport = String(input.support ?? "auto") as "auto" | "floor" | "wall" | "element";
  const clearanceInput = input.clearanceMm as RecordInput | undefined;
  if (!clearanceInput) {
    throw new Error("A implantação relacional precisa da folga escolhida no plano; a ferramenta não define distâncias de projeto automaticamente.");
  }
  let clearance = {
    minimum: Number(clearanceInput.minimum),
    target: Number(clearanceInput.target),
    maximum: Number(clearanceInput.maximum),
  };
  if (![clearance.minimum, clearance.target, clearance.maximum].every(Number.isFinite)
    || clearance.minimum < 0
    || clearance.minimum > clearance.target
    || clearance.target > clearance.maximum) {
    throw new Error("A implantação relacional recebeu uma faixa de folga inválida.");
  }
  const referenceCenter = renderedElementCenterMm(reference);
  const referenceSize = renderedElementSizeMm(reference);
  const referenceVertical = renderedElementVerticalIntervalMm(reference);
  const sourceSize = renderedElementSizeMm(source);
  // An installer quotes a wall-hung module by the height of its underside
  // from the floor. Accept that language and translate it into the vertical
  // clearance the geometry works with, keeping the width of the planned band.
  let bottomHeightMm: number | null = null;
  if (typeof input.bottomHeightMm === "number" && Number.isFinite(input.bottomHeightMm)
    && (relation === "above" || relation === "below" || relation === "stacked")) {
    bottomHeightMm = input.bottomHeightMm;
    const derivedTarget = relation === "below"
      ? referenceVertical[0] - (bottomHeightMm + sourceSize[1])
      : bottomHeightMm - referenceVertical[1];
    if (derivedTarget < 0) {
      throw new Error("A altura de " + Math.round(bottomHeightMm) + " mm para a face inferior faria o alvo atravessar a referência (topo da referência a " + Math.round(referenceVertical[1]) + " mm do piso). Escolha uma altura acima do topo da referência ou use clearanceMm.");
    }
    const below = Math.max(0, clearance.target - clearance.minimum);
    const above = Math.max(0, clearance.maximum - clearance.target);
    clearance = {
      minimum: Math.max(0, derivedTarget - below),
      target: derivedTarget,
      maximum: derivedTarget + above,
    };
  }
  const typology = installationGuidance({
    target: { name: source.name, category: source.catalogRef?.itemId ?? null, dimensionsMm: source.dimensionsMm },
    reference: observedTypologyDescriptor(scene, reference),
    relation,
    support: requestedSupport === "auto" ? null : requestedSupport,
  });
  const typologyWarnings: string[] = [];
  // Régua da pessoa e da parede: atividade na superfície da referência, faixa
  // de face inferior, linhas que a parede já tem e candidatos de altura.
  const vertical = relation === "above" || relation === "below" || relation === "stacked";
  const environment = vertical ? buildEnvironmentReading(scene, { focusElementIds: [reference.id] }) : null;
  const referenceReading = environment?.elements.find((entry) => entry.elementId === reference.id) ?? null;
  const surfaceReading = environment?.surfaces.find((entry) => entry.elementId === reference.id) ?? null;
  const activity = surfaceReading
    ? { activity: surfaceReading.activity, label: ACTIVITY_LABELS[surfaceReading.activity], confidence: surfaceReading.confidence, basis: surfaceReading.basis }
    : null;
  const band = vertical && relation !== "stacked"
    ? verticalPlacementBand({
        activity: surfaceReading?.activity ?? "unknown",
        activityConfidence: surfaceReading?.confidence ?? "low",
        pairBottomHeightMm: typology.rule?.bottomHeightMm ?? null,
        pairClearanceMm: typology.rule?.typicalMm ?? null,
        pairMinimumMm: typology.rule?.minimumMm ?? null,
        upperKind: typology.targetTypology.kind,
      })
    : null;
  const wallLines = referenceReading?.wallId
    ? (environment?.walls.find((wall) => wall.wallId === referenceReading.wallId)?.lines ?? [])
    : [];
  const candidatesOfHeight = environment && relation === "above"
    ? heightCandidates({
        reading: environment,
        reference: referenceReading,
        subjectKind: typology.targetTypology.kind,
        subjectHeightMm: sourceSize[1],
        activity: surfaceReading?.activity ?? "unknown",
        activityConfidence: surfaceReading?.confidence ?? "low",
        pairBottomHeightMm: typology.rule?.bottomHeightMm ?? null,
        pairClearanceMm: typology.rule?.typicalMm ?? null,
        pairMinimumMm: typology.rule?.minimumMm ?? null,
      })
    : [];
  if (typology.rule && (relation === "above" || relation === "below" || relation === "stacked")) {
    const rule = typology.rule;
    if (!rule.contactAllowed && clearance.target < rule.minimumMm) {
      const bottomHint = rule.bottomHeightMm ? "; face inferior a " + rule.bottomHeightMm[0] + "–" + rule.bottomHeightMm[1] + " mm do piso" : "";
      typologyWarnings.push("A folga-alvo de " + Math.round(clearance.target) + " mm fica abaixo do mínimo profissional de " + rule.minimumMm + " mm para " + typology.targetTypology.label + " sobre " + (typology.referenceTypology?.label ?? "a referência") + " (típico " + rule.typicalMm[0] + "–" + rule.typicalMm[1] + " mm" + bottomHint + "). Se a folga não foi fixada pelo projetista, emende a decisão espacial do plano antes de implantar.");
    }
    if (rule.maximumTopHeightMm && bottomHeightMm !== null && bottomHeightMm + sourceSize[1] > rule.maximumTopHeightMm) {
      typologyWarnings.push("O topo do alvo ficaria a " + Math.round(bottomHeightMm + sourceSize[1]) + " mm do piso, acima do alcance recomendado de " + rule.maximumTopHeightMm + " mm.");
    }
  }
  // Cota efetiva da face inferior desta implantação (para alcance e operação).
  const effectiveBottom = relation === "above"
    ? referenceVertical[1] + clearance.target
    : relation === "below"
      ? referenceVertical[0] - clearance.target - sourceSize[1]
      : null;
  const storageTarget = ["upper_cabinet", "shelf", "unknown"].includes(typology.targetTypology.kind);
  const reach = effectiveBottom !== null && storageTarget
    ? reachAssessment({ bottomMm: effectiveBottom, topMm: effectiveBottom + sourceSize[1] })
    : null;
  const operation = effectiveBottom !== null
    ? operationEnvelopeAssessment({
        opening: inferOpeningKind({ name: source.name }),
        bottomMm: effectiveBottom,
        topMm: effectiveBottom + sourceSize[1],
        projectionMm: sourceSize[1],
        standoffMm: referenceReading?.depthMm ?? referenceSize[2],
      })
    : null;
  if (band?.bottomHeightMm && effectiveBottom !== null && storageTarget && relation === "above") {
    const [low, high] = band.bottomHeightMm;
    const aligned = wallLines.some((line) => Math.abs(line.heightMm - effectiveBottom) <= DATUM_TOLERANCE_MM || Math.abs(line.heightMm - (effectiveBottom + sourceSize[1])) <= DATUM_TOLERANCE_MM);
    if ((effectiveBottom < low - DATUM_TOLERANCE_MM || effectiveBottom > high + DATUM_TOLERANCE_MM) && !(band.lineMayOverride && aligned)) {
      typologyWarnings.push("A face inferior efetiva a " + Math.round(effectiveBottom) + " mm fica fora da faixa de uso para " + (activity?.label ?? "atividade indefinida") + " (" + low + "–" + high + " mm)" + (band.lineMayOverride && wallLines.length ? "; alternativa: alinhar a uma linha existente da parede (ver heightCandidates)" : "") + ".");
    }
  }
  if (reach && (reach.status === "out_of_reach" || reach.status === "occasional")) typologyWarnings.push(reach.message);
  if (operation?.status === "head_zone" && operation.message) typologyWarnings.push(operation.message);
  const typologyGuidance: SemanticPlacementTypologyGuidance = {
    targetTypology: typology.targetTypology,
    referenceTypology: typology.referenceTypology,
    rule: typology.rule,
    guidance: typology.guidance,
    effectiveClearanceMm: relation === "above" || relation === "below" || relation === "stacked" ? { ...clearance } : null,
    bottomHeightMm,
    warnings: typologyWarnings,
    activity,
    band: band ? { bottomHeightMm: band.bottomHeightMm, clearanceMm: band.clearanceMm, lineMayOverride: band.lineMayOverride, basis: band.basis } : null,
    wallLines: wallLines.map((line) => ({ heightMm: line.heightMm, kind: line.kind, family: line.family, ...(line.sourceName ? { sourceName: line.sourceName } : {}) })),
    heightCandidates: candidatesOfHeight,
    reach: reach?.message ?? null,
    operation: operation?.status === "head_zone" ? operation.message : null,
  };
  const yaw = reference.transform.rotationRad[1];
  const widthAxis: Vec2 = [Math.cos(yaw), -Math.sin(yaw)];
  const depthAxis: Vec2 = [Math.sin(yaw), Math.cos(yaw)];
  const referenceWall = semanticReferenceWall(scene, reference, typeof input.wallId === "string" ? input.wallId : null);
  const support = requestedSupport === "auto"
    ? relation === "stacked"
      ? "element"
      : relation === "above" || relation === "below"
        ? referenceWall ? "wall" : "element"
        : "floor"
    : requestedSupport;
  const alignmentOptions: Array<"front" | "back" | "center"> = requestedAlignment !== "auto"
    ? [requestedAlignment]
    : referenceWall
      ? ["back", "front"]
      : relation === "beside"
        ? ["front", "center"]
        : ["center", "front"];
  // Um lado pode ser pedido de forma inequívoca: o mais próximo de uma parede
  // ou de um elemento, ou o mais distante de um elemento. Resolve para
  // left/right medindo os dois candidatos, sem depender da convenção de "esquerda".
  const sideAnchor = resolveSideAnchor(scene, input);
  const resolvedPreferredSide: "auto" | "left" | "right" | "front" | "back" = sideAnchor && relation === "beside"
    ? (() => {
        const distances = (["left", "right"] as const).map((side) => {
          const widthDistance = referenceSize[0] / 2 + sourceSize[0] / 2 + clearance.target;
          const along = side === "left" ? -widthDistance : widthDistance;
          const center: Vec3 = [referenceCenter[0] + widthAxis[0] * along, referenceCenter[1], referenceCenter[2] + widthAxis[1] * along];
          return { side, distance: sideAnchor.distance(center) };
        });
        const sorted = distances.sort((left, right) => sideAnchor.mode === "towards" ? left.distance - right.distance : right.distance - left.distance);
        return sorted[0]!.side;
      })()
    : preferredSide;
  const sideOptions: Array<"left" | "right" | "front" | "back" | "center"> = relation === "beside"
    ? resolvedPreferredSide === "left" || resolvedPreferredSide === "right" ? [resolvedPreferredSide] : ["left", "right"]
    : relation === "in_front_of"
      ? ["front"]
      : relation === "behind"
        ? ["back"]
        : relation === "against"
          ? preferredSide === "front" || preferredSide === "back" ? [preferredSide] : ["back", "front"]
          : ["center"];
  const maxCandidates = Math.max(1, Math.min(8, Math.round(finiteMm(input.maxCandidates, 4))));
  const floorElevationMm = checkElementInstallation(scene, reference).floor.floorElevationMm;
  const raw: SemanticPlacementCandidateInternal[] = [];
  const evaluateSides = (sides: ReadonlyArray<"left" | "right" | "front" | "back" | "center">, into: SemanticPlacementCandidateInternal[], limit: number) => {
  for (const side of sides) {
    for (const alignment of alignmentOptions) {
      if (into.length >= limit) break;
      const widthDistance = referenceSize[0] / 2 + sourceSize[0] / 2 + clearance.target;
      const depthDistance = referenceSize[2] / 2 + sourceSize[2] / 2 + clearance.target;
      let alongWidth = side === "left" ? -widthDistance : side === "right" ? widthDistance : 0;
      let alongDepth = side === "front" ? depthDistance : side === "back" ? -depthDistance : 0;
      if (side === "center") {
        alongDepth = alignment === "front"
          ? referenceSize[2] / 2 - sourceSize[2] / 2
          : alignment === "back"
            ? -referenceSize[2] / 2 + sourceSize[2] / 2
            : 0;
      } else if (side === "left" || side === "right") {
        alongDepth = alignment === "front"
          ? referenceSize[2] / 2 - sourceSize[2] / 2
          : alignment === "back"
            ? -referenceSize[2] / 2 + sourceSize[2] / 2
            : 0;
      }
      const physicalCenter: Vec3 = [
        referenceCenter[0] + widthAxis[0] * alongWidth + depthAxis[0] * alongDepth,
        semanticVerticalCenter(relation, support, sourceSize[1], referenceVertical, floorElevationMm, clearance.target),
        referenceCenter[2] + widthAxis[1] * alongWidth + depthAxis[1] * alongDepth,
      ];
      let preview = semanticPreviewAtPhysicalCenter(source, physicalCenter, yaw);
      let wallId: string | null = null;
      const reasons: string[] = [];

      if (support === "wall") {
        if (!referenceWall) {
          reasons.push("Nenhuma parede coerente com a referência foi encontrada para sustentar o elemento.");
        } else {
          wallId = referenceWall.id;
          try {
            const mount = resolveWallMountSurface(scene, referenceWall, physicalCenter, {
              roomId: typeof input.roomId === "string" ? input.roomId : undefined,
              face: "interior",
            });
            const placement = resolveSurfacePlacement(
              scene,
              physicalCenter,
              mount.surface,
              sourceSize,
              preview.transform.rotationRad,
              { snap: false },
            );
            const rootedPreview = semanticPreviewAtPhysicalCenter(source, placement.positionMm, placement.rotationRad[1]);
            preview = {
              ...rootedPreview,
              transform: {
                ...rootedPreview.transform,
                rotationRad: placement.rotationRad,
              },
              extensions: withElementPlacementSurface(rootedPreview, mount.surface),
            };
          } catch (error) {
            reasons.push(error instanceof Error ? error.message : "Não foi possível resolver a face da parede.");
          }
        }
      } else if (support === "floor") {
        preview = {
          ...preview,
          extensions: withElementPlacementSurface(preview, {
            kind: "floor",
            pointMm: [preview.transform.translationMm[0], floorElevationMm, preview.transform.translationMm[2]],
            normal: [0, 1, 0],
          }),
        };
      } else {
        preview = { ...preview, extensions: withElementPlacementSurface(preview, null) };
      }

      const geometryClearance = checkElementClearance(scene, preview);
      const grounded = checkElementGrounded(scene, preview);
      const installation = checkElementInstallation(scene, preview);
      if (geometryClearance.collisions.length) {
        reasons.push(`Colide com ${geometryClearance.collisions.map((collision) => collision.name).join(", ")}.`);
      }
      if (installation.support.status !== "supported") {
        reasons.push(`Apoio ${support} não comprovado (${installation.support.status}).`);
      }
      const relationIssue = semanticRelationIssue(relation, reference.id, installation, preview, reference, clearance);
      if (relationIssue) reasons.push(relationIssue);
      if (!semanticCenterInsideRoom(scene, renderedElementCenterMm(preview))) {
        reasons.push("O centro físico ficaria fora dos limites do ambiente.");
      }
      if (wallId && semanticOpeningOverlap(scene, preview, wallId)) {
        reasons.push("A implantação invade o vão de uma porta ou janela dessa parede.");
      }

      const valid = reasons.length === 0;
      const geometryScore = Math.max(0, 100
        - geometryClearance.collisions.length * 38
        - (installation.support.status === "supported" ? 0 : 42)
        - (relationIssue ? 24 : 0)
        - (valid ? 0 : 8));
      const designScore = Math.max(0, Math.min(100,
        56
        + (alignment === "back" && referenceWall ? 18 : alignment === "front" ? 10 : 6)
        + (resolvedPreferredSide !== "auto" && resolvedPreferredSide === side ? 12 : 0)
        + (wallId ? 8 : 0),
      ));
      const totalScore = Math.round((geometryScore * 0.74 + designScore * 0.26) * 10) / 10;
      const rotationDeg = preview.transform.rotationRad.map((value) => Math.round(value * 180 / Math.PI * 10) / 10) as Vec3;
      const label = `${semanticSideLabel(side)} · alinhamento ${semanticAlignmentLabel(alignment)}`;
      into.push({
        preview,
        result: {
          candidateId: `semantic-${into.length + 1}-${side}-${alignment}`,
          label,
          positionMm: preview.transform.translationMm.map(roundMm) as Vec3,
          rotationDeg,
          side,
          alignment,
          wallId,
          geometryScore,
          designScore,
          totalScore,
          valid,
          reasons,
          clearance: geometryClearance as unknown as Record<string, unknown>,
          grounded: grounded as unknown as Record<string, unknown>,
          installation: installation as unknown as Record<string, unknown>,
        },
      });
    }
  }
  };
  evaluateSides(sideOptions, raw, maxCandidates);

  const candidates = raw.sort((left, right) => (
    Number(right.result.valid) - Number(left.result.valid)
    || right.result.totalScore - left.result.totalScore
  ));
  const recommended = candidates.find((candidate) => candidate.result.valid) ?? null;
  const blockedReasons = recommended
    ? []
    : [...new Set(candidates.flatMap((candidate) => candidate.result.reasons))].slice(0, 8);
  if (recommended && referenceWall && relation === "beside" && support === "floor") {
    const wallGap = (recommended.result.installation as { nearestWalls?: Array<{ wallId: string; gapMm: number }> }).nearestWalls
      ?.find((wall) => wall.wallId === referenceWall.id)?.gapMm ?? null;
    if (recommended.result.alignment === "front" && wallGap !== null && wallGap > 20) {
      typologyWarnings.push(`Alinhar as frentes deixa ${Math.round(wallGap)} mm entre o fundo do alvo e a parede ${referenceWall.id} (profundidades diferentes). Para encostar o fundo na parede use alignment=back — as frentes ficam em planos distintos; escolha uma das duas intenções e registre-a no plano.`);
    }
    const interferences = (recommended.result.clearance as { interferences?: Array<{ name: string }> }).interferences ?? [];
    if (interferences.length) {
      typologyWarnings.push(`Pontos de instalação cobertos pelo alvo nesta posição: ${interferences.map((entry) => entry.name).join(", ")} — interferência a prever no detalhamento (recorte ou acesso), não colisão; não afaste o móvel da parede por causa deles.`);
    }
  }
  // When a requested side fails, diagnose the opposite side in the same call:
  // the model learns which side is free instead of guessing it next round.
  if (!recommended && relation === "beside" && (resolvedPreferredSide === "left" || resolvedPreferredSide === "right")) {
    const other = resolvedPreferredSide === "left" ? "right" : "left";
    const alternative: SemanticPlacementCandidateInternal[] = [];
    evaluateSides([other], alternative, Math.max(1, alignmentOptions.length));
    const viable = alternative.find((candidate) => candidate.result.valid);
    blockedReasons.push(viable
      ? `O lado ${resolvedPreferredSide} (${semanticSideLabel(resolvedPreferredSide)}) não é viável; o lado ${other} (${semanticSideLabel(other)}) é viável: ${viable.result.label}, posição ${viable.result.positionMm.join(", ")} mm. Repita com preferredSide=${other} ou com towardsWallId/awayFromElementId.`
      : `Nenhum dos dois lados (left/right) é viável para esta relação: ${[...new Set(alternative.flatMap((candidate) => candidate.result.reasons))].slice(0, 4).join(" ") || "sem candidato"}`);
  }
  return { recommended, candidates, blockedReasons, typologyGuidance };
}

/**
 * Âncora de lado inequívoca: towardsWallId (mais próximo da parede),
 * towardsElementId (mais próximo do elemento) ou awayFromElementId (mais
 * distante do elemento). Devolve uma função de distância no plano.
 */
function resolveSideAnchor(
  scene: SceneDocument,
  input: RecordInput,
): { mode: "towards" | "away"; distance: (centerMm: Vec3) => number } | null {
  const wallId = typeof input.towardsWallId === "string" ? input.towardsWallId : null;
  if (wallId) {
    const wall = scene.walls.find((candidate) => candidate.id === wallId);
    if (!wall) throw new Error(`towardsWallId aponta para uma parede inexistente: ${wallId}.`);
    return {
      mode: "towards",
      distance: (center) => distanceToSegment2D([center[0], center[2]], wall.startMm, wall.endMm),
    };
  }
  const elementKey = typeof input.towardsElementId === "string"
    ? { id: input.towardsElementId, mode: "towards" as const }
    : typeof input.awayFromElementId === "string"
      ? { id: input.awayFromElementId, mode: "away" as const }
      : null;
  if (!elementKey) return null;
  const anchor = scene.elements.find((candidate) => candidate.id === elementKey.id);
  if (!anchor) throw new Error(`${elementKey.mode === "towards" ? "towardsElementId" : "awayFromElementId"} aponta para um elemento inexistente: ${elementKey.id}.`);
  const anchorCenter = renderedElementCenterMm(anchor);
  return {
    mode: elementKey.mode,
    distance: (center) => Math.hypot(center[0] - anchorCenter[0], center[2] - anchorCenter[2]),
  };
}

function distanceToSegment2D(point: Vec2, start: Vec2, end: Vec2): number {
  const dx = end[0] - start[0];
  const dz = end[1] - start[1];
  const lengthSquared = dx * dx + dz * dz;
  if (lengthSquared < 1e-6) return Math.hypot(point[0] - start[0], point[1] - start[1]);
  const t = Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dz) / lengthSquared));
  return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dz));
}

function semanticPreviewAtPhysicalCenter(source: SceneElement, centerMm: Vec3, yaw: number): SceneElement {
  const envelope = renderedElementEnvelope(source);
  const localCenter: Vec3 = [
    (envelope.minimumMm[0] + envelope.maximumMm[0]) / 2,
    (envelope.minimumMm[1] + envelope.maximumMm[1]) / 2,
    (envelope.minimumMm[2] + envelope.maximumMm[2]) / 2,
  ];
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return {
    ...source,
    transform: {
      ...source.transform,
      translationMm: [
        centerMm[0] - localCenter[0] * cos - localCenter[2] * sin,
        centerMm[1] - localCenter[1],
        centerMm[2] + localCenter[0] * sin - localCenter[2] * cos,
      ].map(roundMm) as Vec3,
      rotationRad: [0, yaw, 0],
    },
  };
}

function sameVec3(left: Vec3, right: Vec3, tolerance = 0.01): boolean {
  return left.every((value, index) => Math.abs(value - right[index]!) <= tolerance);
}

function sameTransformAndSurface(left: SceneElement, right: SceneElement): boolean {
  return sameVec3(left.transform.translationMm, right.transform.translationMm)
    && sameVec3(left.transform.rotationRad, right.transform.rotationRad, 0.00001)
    && JSON.stringify(readElementPlacementSurface(useEditorStore.getState().scene, left))
      === JSON.stringify(readElementPlacementSurface(useEditorStore.getState().scene, right));
}

function semanticVerticalCenter(
  relation: string,
  support: string,
  heightMm: number,
  referenceVertical: [number, number],
  floorElevationMm: number,
  clearanceMm: number,
): number {
  if (relation === "above" || relation === "stacked") return referenceVertical[1] + clearanceMm + heightMm / 2;
  if (relation === "below") return referenceVertical[0] - clearanceMm - heightMm / 2;
  if (support === "wall") return Math.max(heightMm / 2, referenceVertical[0] + heightMm / 2);
  return floorElevationMm + heightMm / 2;
}

function semanticReferenceWall(scene: SceneDocument, reference: SceneElement, explicitWallId: string | null): Wall | null {
  if (explicitWallId) return scene.walls.find((wall) => wall.id === explicitWallId) ?? null;
  const surface = readElementPlacementSurface(scene, reference);
  if (surface?.kind === "wall" && surface.wallId) {
    const wall = scene.walls.find((candidate) => candidate.id === surface.wallId);
    if (wall) return wall;
  }
  const installation = checkElementInstallation(scene, reference);
  const allowedGap = Math.max(200, renderedElementSizeMm(reference)[2]);
  const nearest = installation.nearestWalls.find((wall) => wall.gapMm <= allowedGap);
  return nearest ? scene.walls.find((wall) => wall.id === nearest.wallId) ?? null : null;
}

function semanticRelationIssue(
  relation: string,
  referenceId: string,
  installation: ReturnType<typeof checkElementInstallation>,
  preview: SceneElement,
  reference: SceneElement,
  clearance: { minimum: number; target: number; maximum: number },
): string | null {
  const measured = installation.nearestElements.find((candidate) => candidate.elementId === referenceId);
  if (!measured) return "A referência não apareceu entre os encontros geométricos mensuráveis.";
  const tolerance = 3;
  if (relation === "above" || relation === "stacked") {
    if (measured.position !== "below") return "O alvo não ficou geometricamente acima da referência.";
    if (measured.verticalGapMm < clearance.minimum - tolerance || measured.verticalGapMm > clearance.maximum + tolerance) {
      return `A folga vertical resultante (${measured.verticalGapMm} mm) está fora da faixa definida.`;
    }
  } else if (relation === "below") {
    if (measured.position !== "above") return "O alvo não ficou geometricamente abaixo da referência.";
    if (measured.verticalGapMm < clearance.minimum - tolerance || measured.verticalGapMm > clearance.maximum + tolerance) {
      return `A folga vertical resultante (${measured.verticalGapMm} mm) está fora da faixa definida.`;
    }
  } else if (relation === "beside") {
    if (measured.position !== "beside") return "O alvo não ficou lateralmente relacionado à referência.";
    if (measured.planGapMm < clearance.minimum - tolerance || measured.planGapMm > clearance.maximum + tolerance) {
      return `A folga lateral resultante (${measured.planGapMm} mm) está fora da faixa definida.`;
    }
  } else {
    const previewCenter = renderedElementCenterMm(preview);
    const referenceCenter = renderedElementCenterMm(reference);
    const yaw = reference.transform.rotationRad[1];
    const depthAxis: Vec2 = [Math.sin(yaw), Math.cos(yaw)];
    const signedDepth = (previewCenter[0] - referenceCenter[0]) * depthAxis[0]
      + (previewCenter[2] - referenceCenter[2]) * depthAxis[1];
    if (relation === "in_front_of" && signedDepth <= 0) return "O alvo não ficou à frente da referência.";
    if ((relation === "behind" || relation === "against") && signedDepth >= 0) return "O alvo não ficou atrás/contra a referência.";
  }
  return null;
}

function semanticCenterInsideRoom(scene: SceneDocument, centerMm: Vec3): boolean {
  if (!scene.rooms.length) return true;
  const point: Vec2 = [centerMm[0], centerMm[2]];
  return scene.rooms.some((room) => pointInsidePolygon(point, room.boundary)
    && !room.holes.some((hole) => pointInsidePolygon(point, hole)));
}

function pointInsidePolygon(point: Vec2, polygon: Vec2[]): boolean {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index]!;
    const previousPoint = polygon[previous]!;
    const intersects = ((currentPoint[1] > point[1]) !== (previousPoint[1] > point[1]))
      && point[0] < (previousPoint[0] - currentPoint[0]) * (point[1] - currentPoint[1])
        / ((previousPoint[1] - currentPoint[1]) || Number.EPSILON) + currentPoint[0];
    if (intersects) inside = !inside;
  }
  return inside;
}

function semanticOpeningOverlap(scene: SceneDocument, element: SceneElement, wallId: string): boolean {
  const wall = scene.walls.find((candidate) => candidate.id === wallId);
  if (!wall) return false;
  const dx = wall.endMm[0] - wall.startMm[0];
  const dz = wall.endMm[1] - wall.startMm[1];
  const length = Math.hypot(dx, dz);
  if (length < 1) return false;
  const tangent: Vec2 = [dx / length, dz / length];
  const center = renderedElementCenterMm(element);
  const size = renderedElementSizeMm(element);
  const yaw = element.transform.rotationRad[1];
  const widthAxis: Vec2 = [Math.cos(yaw), -Math.sin(yaw)];
  const depthAxis: Vec2 = [Math.sin(yaw), Math.cos(yaw)];
  const centerAlong = (center[0] - wall.startMm[0]) * tangent[0] + (center[2] - wall.startMm[1]) * tangent[1];
  const halfAlong = size[0] / 2 * Math.abs(widthAxis[0] * tangent[0] + widthAxis[1] * tangent[1])
    + size[2] / 2 * Math.abs(depthAxis[0] * tangent[0] + depthAxis[1] * tangent[1]);
  const vertical = renderedElementVerticalIntervalMm(element);
  return scene.openings.some((opening) => {
    if (opening.wallId !== wallId) return false;
    // Opening offsets are measured from the start of the wall, not from the
    // opening centre (the renderer and scene validator use the same contract).
    const openingMinimum = opening.offsetMm;
    const openingMaximum = opening.offsetMm + opening.widthMm;
    const horizontalOverlap = centerAlong + halfAlong > openingMinimum + 2 && centerAlong - halfAlong < openingMaximum - 2;
    const openingTop = opening.sillMm + opening.heightMm;
    const verticalOverlap = vertical[1] > opening.sillMm + 2 && vertical[0] < openingTop - 2;
    return horizontalOverlap && verticalOverlap;
  });
}

function semanticSideLabel(side: SemanticPlacementCandidateResult["side"]): string {
  return ({ left: "lado esquerdo", right: "lado direito", front: "à frente", back: "atrás", center: "centralizado" })[side];
}

function semanticAlignmentLabel(alignment: SemanticPlacementCandidateResult["alignment"]): string {
  return ({ front: "frontal", back: "pelo fundo/parede", center: "central" })[alignment];
}

function finiteMm(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function roundMm(value: number): number {
  return Math.round(value * 10) / 10;
}

function previewElementFromCatalog(item: CatalogItem): SceneElement {
  const engineering = item.engineering;
  return {
    id: `placement-preview-${item.id}`,
    kind: engineering
      ? "model"
      : item.root === "Estruturas"
        ? "panel"
        : item.type === "architecture"
          ? "geometry"
          : "module",
    revision: 0,
    name: item.name,
    layerId: "layer-architecture",
    transform: {
      translationMm: [0, 0, 0],
      rotationRad: [0, 0, 0],
      scale: [1, 1, 1],
    },
    dimensionsMm: {
      width: Math.max(10, item.dimensionsMm.width),
      height: Math.max(10, item.dimensionsMm.height),
      depth: Math.max(10, item.dimensionsMm.depth),
    },
    catalogRef: { itemId: item.stableItemId, revision: item.revision ?? "1.0.0" },
    materialId: item.color,
    materialAssignments: {},
    locked: false,
    visible: true,
    parameters: {
      ...(item.parameterDefaults ?? {}),
      resize: item.resize ?? null,
      formulas: item.formulas ?? {},
    },
    componentVisibility: {},
    instanceOverrides: {},
    operationIds: [],
    extensions: engineering
      ? {
          importedAssetId: engineering.renderAssetId,
          importedFileName: `${item.stableItemId}.glb`,
          importedMimeType: "model/gltf-binary",
          normalizedUnit: "m",
          pivot: "bounds-center",
          engineeringSourceAssetId: engineering.sourceAssetId,
          engineeringSourceFileName: engineering.sourceFileName,
          engineeringComponentRoot: engineering.root,
          engineeringMeshBindings: engineering.meshBindings,
          engineeringDynamicAssetId: engineering.dynamicAssetId,
        }
      : {},
  };
}

function addWall(input: RecordInput, turnId: string): unknown {
  const wallId = withAgentTurn(turnId, () => useEditorStore.getState().addWall(input.startMm as Vec2, input.endMm as Vec2));
  return { wall: wallSnapshot(requireWall(wallId)) };
}

function updateWall(input: RecordInput, turnId: string): unknown {
  const wallId = String(input.wallId);
  requireWall(wallId);
  withAgentTurn(turnId, () => useEditorStore.getState().updateWall(wallId, input.patch as Partial<Wall>));
  return { wall: wallSnapshot(requireWall(wallId)) };
}

function moveWallVertex(input: RecordInput, turnId: string): unknown {
  const references = input.references as Array<{ wallId: string; endpoint: "startMm" | "endMm" }>;
  references.forEach((reference) => requireWall(reference.wallId));
  withAgentTurn(turnId, () => useEditorStore.getState().moveWallVertex(references, input.targetMm as Vec2));
  return { walls: [...new Set(references.map((reference) => reference.wallId))].map((id) => wallSnapshot(requireWall(id))) };
}

function addOpening(input: RecordInput, turnId: string): unknown {
  const wallId = String(input.wallId);
  requireWall(wallId);
  const openingId = withAgentTurn(turnId, () => useEditorStore.getState().addOpening(
    input.kind as "door" | "window",
    wallId,
    Number(input.offsetMm),
  ));
  if (!openingId) throw new Error("O vão não coube na parede na posição pedida.");
  return { opening: openingSnapshot(requireOpening(openingId)) };
}

function updateOpening(input: RecordInput, turnId: string): unknown {
  const openingId = String(input.openingId);
  requireOpening(openingId);
  withAgentTurn(turnId, () => useEditorStore.getState().updateOpening(openingId, input.patch as Partial<Opening>));
  return { opening: openingSnapshot(requireOpening(openingId)) };
}

function addPrimitive(input: RecordInput, turnId: string): unknown {
  const createdId = withAgentTurn(turnId, () => useEditorStore.getState().addPrimitive(
    input.shape as "box" | "cylinder" | "sphere",
    input.positionMm as Vec3 | undefined,
    input.dimensionsMm as SceneElement["dimensionsMm"] | undefined,
  ));
  return { element: snapshot(requireElement(createdId)) };
}

function addMeasurement(input: RecordInput, turnId: string): unknown {
  const createdId = withAgentTurn(turnId, () => useEditorStore.getState().addMeasurement(
    input.pointsMm as Vec3[],
    input.scope as Measurement["scope"] | undefined,
  ));
  return { measurement: measurementSnapshot(requireMeasurement(createdId)) };
}

function updateMeasurement(input: RecordInput, turnId: string): unknown {
  const measurementId = String(input.measurementId);
  requireMeasurement(measurementId);
  withAgentTurn(turnId, () => useEditorStore.getState().updateMeasurement(measurementId, input.patch as Partial<Measurement>));
  return { measurement: measurementSnapshot(requireMeasurement(measurementId)) };
}

function addLayer(input: RecordInput, turnId: string): unknown {
  const createdId = withAgentTurn(turnId, () => useEditorStore.getState().addLayer(input.name ? String(input.name) : undefined));
  return { layer: layerSnapshot(requireLayer(createdId)) };
}

function updateLayer(input: RecordInput, turnId: string): unknown {
  const layerId = String(input.layerId);
  requireLayer(layerId);
  withAgentTurn(turnId, () => useEditorStore.getState().updateLayer(layerId, input.patch as Partial<SceneLayer>));
  return { layer: layerSnapshot(requireLayer(layerId)) };
}

function reorderLayer(input: RecordInput, turnId: string): unknown {
  const layerId = String(input.layerId);
  requireLayer(layerId);
  withAgentTurn(turnId, () => useEditorStore.getState().reorderLayer(layerId, input.direction as -1 | 1));
  return { layers: useEditorStore.getState().scene.layers.map(layerSnapshot) };
}

function deleteLayer(input: RecordInput, turnId: string): unknown {
  const layerId = String(input.layerId);
  requireLayer(layerId);
  withAgentTurn(turnId, () => useEditorStore.getState().deleteLayer(layerId));
  return { deletedLayerId: layerId, layers: useEditorStore.getState().scene.layers.map(layerSnapshot) };
}

function addOperation(input: RecordInput, turnId: string): unknown {
  const operation = input.operation as unknown as SceneOperation;
  withAgentTurn(turnId, () => useEditorStore.getState().addOperation(operation));
  const stored = useEditorStore.getState().scene.operations.find((candidate) => candidate.id === operation.id);
  if (!stored) throw new Error("A operação não foi aceita pela cena.");
  return { operation: stored as unknown as Record<string, unknown> };
}

function removeOperation(input: RecordInput, turnId: string): unknown {
  const operationId = String(input.operationId);
  if (!useEditorStore.getState().scene.operations.some((operation) => operation.id === operationId)) {
    throw new Error(`Operação ${operationId} não foi encontrada.`);
  }
  withAgentTurn(turnId, () => useEditorStore.getState().removeOperation(operationId));
  return { removedOperationId: operationId };
}

function setPartAttribute(input: RecordInput, turnId: string): unknown {
  const target = requireElement(String(input.elementId));
  withAgentTurn(turnId, () => useEditorStore.getState().setPartAttribute(
    target.id,
    String(input.path),
    String(input.attribute),
    input.value as string | number | null,
    input.kind as "edit" | "action",
  ));
  return { element: snapshot(requireElement(target.id)) };
}

function setCamera(input: RecordInput): unknown {
  const mode = input.mode as "plan" | "front" | "3d";
  useEditorStore.getState().setCamera(mode, input.patch as Partial<CameraState>);
  return { camera: useEditorStore.getState().cameraByMode[mode] as unknown as Record<string, unknown> };
}

function requireWall(id: string, scene = useEditorStore.getState().scene): Wall {
  const wall = scene.walls.find((candidate) => candidate.id === id);
  if (!wall) throw new Error(`Parede ${id} não foi encontrada.`);
  if (wall.locked) throw new Error(`A parede ${id} está bloqueada.`);
  return wall;
}

function requireOpening(id: string, scene = useEditorStore.getState().scene): Opening {
  const opening = scene.openings.find((candidate) => candidate.id === id);
  if (!opening) throw new Error(`Vão ${id} não foi encontrado.`);
  return opening;
}

function requireMeasurement(id: string, scene = useEditorStore.getState().scene): Measurement {
  const measurement = scene.measurements.find((candidate) => candidate.id === id);
  if (!measurement) throw new Error(`Cota ${id} não foi encontrada.`);
  return measurement;
}

function requireLayer(id: string, scene = useEditorStore.getState().scene): SceneLayer {
  const layer = scene.layers.find((candidate) => candidate.id === id);
  if (!layer) throw new Error(`Camada ${id} não foi encontrada.`);
  return layer;
}

function wallSnapshot(wall: Wall): Record<string, unknown> {
  return {
    id: wall.id,
    startMm: wall.startMm,
    endMm: wall.endMm,
    heightMm: wall.heightMm,
    thicknessMm: wall.thicknessMm,
    openingIds: wall.openingIds,
    materialId: wall.materialId,
    layerId: wall.layerId,
    locked: wall.locked,
  };
}

function openingSnapshot(opening: Opening): Record<string, unknown> {
  return {
    id: opening.id,
    kind: opening.kind,
    wallId: opening.wallId,
    offsetMm: opening.offsetMm,
    widthMm: opening.widthMm,
    heightMm: opening.heightMm,
    sillMm: opening.sillMm,
    materialId: opening.materialId,
  };
}

function measurementSnapshot(measurement: Measurement): Record<string, unknown> {
  return { ...measurement } as unknown as Record<string, unknown>;
}

function layerSnapshot(layer: SceneLayer): Record<string, unknown> {
  return { ...layer } as unknown as Record<string, unknown>;
}

function addAutomaticVerification(
  definition: CapabilityDefinition,
  input: RecordInput,
  output: unknown,
): unknown {
  if (definition.plane !== "client" || definition.effect === "read" || !definition.name.startsWith("scene.")) return output;
  if (["scene.selection.set", "scene.camera.set", "scene.view.mode.set", "scene.undo", "scene.redo", "scene.save"].includes(definition.name)) return output;
  const scene = useEditorStore.getState().scene;
  const candidateIds = new Set<string>();
  if (typeof input.elementId === "string") candidateIds.add(input.elementId);
  if (Array.isArray(input.elementIds)) input.elementIds.forEach((value) => typeof value === "string" && candidateIds.add(value));
  if (output && typeof output === "object") {
    const value = output as RecordInput;
    const element = value.element as RecordInput | undefined;
    if (typeof element?.id === "string") candidateIds.add(element.id);
    const elements = value.elements as RecordInput[] | undefined;
    elements?.forEach((entry) => typeof entry.id === "string" && candidateIds.add(entry.id));
  }
  const elementChecks = [...candidateIds]
    .map((id) => scene.elements.find((element) => element.id === id))
    .filter((element): element is SceneElement => Boolean(element))
    .map((element) => ({
      element: snapshot(element),
      clearance: checkElementClearance(scene, element),
      grounded: checkElementGrounded(scene, element),
      installation: checkElementInstallation(scene, element),
    }));
  return output && typeof output === "object" && !Array.isArray(output)
    ? { ...(output as RecordInput), verification: { elementChecks, sceneCounts: { walls: scene.walls.length, openings: scene.openings.length, elements: scene.elements.length } } }
    : output;
}

function assertResultBudget(value: unknown): void {
  const json = JSON.stringify(value, (key, item) => key === "dataBase64" && typeof item === "string" ? `[base64:${item.length}]` : item);
  const bytes = new TextEncoder().encode(json).byteLength;
  if (bytes > MAX_RESULT_BYTES) {
    throw new Error("O resultado ficou grande demais para a conversa. Refine o pedido e tente novamente.");
  }
}

function summarizeForAudit(value: unknown): unknown {
  const json = JSON.stringify(value, (key, item) => {
    if (key === "dataBase64" && typeof item === "string") return `[imagem base64: ${item.length} caracteres]`;
    return item;
  });
  if (json.length <= 4_000) return JSON.parse(json);
  return { summary: `${json.slice(0, 3_900)}…`, truncated: true };
}

function captureForAudit(value: unknown): unknown {
  // Viewport captures are the evidence itself. They are intentionally retained
  // here and moved to durable asset storage by the API; ordinary audit values
  // continue through the bounded textual summariser above.
  return value;
}

function auditBatches(records: AgentExecutionRecord[]): AgentExecutionRecord[][] {
  const batches: AgentExecutionRecord[][] = [];
  let current: AgentExecutionRecord[] = [];
  let currentCharacters = 0;
  for (const record of records) {
    const characters = JSON.stringify(record).length;
    if (current.length && (current.length >= 40 || currentCharacters + characters > 12_000_000)) {
      batches.push(current);
      current = [];
      currentCharacters = 0;
    }
    current.push(record);
    currentCharacters += characters;
  }
  if (current.length) batches.push(current);
  return batches;
}

function setSelection(ids: string[]): unknown {
  for (const id of ids) requireSceneTarget(id);
  selectExactly(ids);
  return { selectedIds: useEditorStore.getState().selectedIds };
}

function moveElement(input: RecordInput, turnId: string): unknown {
  const element = requireElement(String(input.elementId));
  const previousTranslationMm = [...element.transform.translationMm] as Vec3;
  const requestedPhysicalCenter = input.physicalCenterMm as Vec3 | undefined;
  const translationMm = requestedPhysicalCenter
    ? semanticPreviewAtPhysicalCenter(element, requestedPhysicalCenter, element.transform.rotationRad[1]).transform.translationMm
    : input.translationMm as Vec3;
  if (sameVec3(element.transform.translationMm, translationMm)) {
    return { element: snapshot(element), previousTranslationMm, unchanged: true };
  }
  selectExactly([element.id]);
  withAgentTurn(turnId, () => useEditorStore.getState().updateSelectedTransform(translationMm));
  return { element: snapshot(requireElement(element.id)), previousTranslationMm, unchanged: false };
}

function nudgeElement(input: RecordInput, turnId: string): unknown {
  const element = requireElement(String(input.elementId));
  const previousTranslationMm = [...element.transform.translationMm] as Vec3;
  const appliedDeltaMm = input.deltaMm as Vec3;
  if (sameVec3(appliedDeltaMm, [0, 0, 0])) {
    return { element: snapshot(element), previousTranslationMm, appliedDeltaMm, unchanged: true };
  }
  const translationMm = previousTranslationMm.map((value, index) => value + appliedDeltaMm[index]!) as Vec3;
  selectExactly([element.id]);
  withAgentTurn(turnId, () => useEditorStore.getState().updateSelectedTransform(translationMm));
  return { element: snapshot(requireElement(element.id)), previousTranslationMm, appliedDeltaMm, unchanged: false };
}

function matchElementAppearance(input: RecordInput, turnId: string): unknown {
  const element = requireElement(String(input.elementId));
  const reference = findElement(String(input.referenceElementId));
  if (element.id === reference.id) throw new Error("O alvo e a referência de acabamento precisam ser diferentes.");
  const materialAssignments = Object.fromEntries(Object.entries(reference.materialAssignments).map(([path, value]) => [
    path,
    { ...value },
  ]));
  const unchanged = element.materialId === reference.materialId
    && JSON.stringify(element.materialAssignments) === JSON.stringify(materialAssignments);
  if (!unchanged) {
    selectExactly([element.id]);
    withAgentTurn(turnId, () => useEditorStore.getState().updateSelected({
      materialId: reference.materialId,
      materialAssignments,
    }));
  }
  return {
    element: snapshot(requireElement(element.id)),
    referenceElementId: reference.id,
    unchanged,
  };
}

function mountElement(input: RecordInput, turnId: string): unknown {
  const element = requireElement(String(input.elementId));
  const previousTransform = {
    translationMm: [...element.transform.translationMm] as Vec3,
    rotationRad: [...element.transform.rotationRad] as Vec3,
  };
  const state = useEditorStore.getState();
  const wallId = String(input.wallId);
  const wall = state.scene.walls.find((candidate) => candidate.id === wallId);
  if (!wall) throw new Error(`Parede ${wallId} não foi encontrada.`);

  const centerMm = input.centerMm as Vec3;
  const mount = resolveWallMountSurface(state.scene, wall, centerMm, {
    face: input.face as WallMountFace,
    roomId: typeof input.roomId === "string" ? input.roomId : undefined,
  });
  const renderedSizeMm = renderedElementSizeMm(element);
  const placement = resolveSurfacePlacement(
    state.scene,
    centerMm,
    mount.surface,
    renderedSizeMm,
    element.transform.rotationRad,
    { snap: false },
  );
  const rootedElement = semanticPreviewAtPhysicalCenter(element, placement.positionMm, placement.rotationRad[1]);
  const mountedPreview: SceneElement = {
    ...rootedElement,
    transform: { ...rootedElement.transform, rotationRad: placement.rotationRad },
    extensions: withElementPlacementSurface(rootedElement, mount.surface),
  };

  if (sameTransformAndSurface(element, mountedPreview)) {
    return {
      element: snapshot(element),
      wallId,
      face: mount.face,
      roomId: mount.referenceRoomId,
      adjacentRoomIds: mount.adjacentRoomIds,
      previousTransform,
      unchanged: true,
    };
  }

  selectExactly([element.id]);
  withAgentTurn(turnId, () => useEditorStore.getState().updateSelected({
    transform: mountedPreview.transform,
    extensions: mountedPreview.extensions,
  }));
  return {
    element: snapshot(requireElement(element.id)),
    wallId,
    face: mount.face,
    roomId: mount.referenceRoomId,
    adjacentRoomIds: mount.adjacentRoomIds,
    previousTransform,
    unchanged: false,
  };
}

function rotateElement(input: RecordInput, turnId: string): unknown {
  const element = requireElement(String(input.elementId));
  const previousRotationRad = [...element.transform.rotationRad] as Vec3;
  const degrees = input.rotationDeg as Vec3;
  const radians: Vec3 = degrees.map((value) => value * Math.PI / 180) as Vec3;
  if (sameVec3(element.transform.rotationRad, radians, 0.00001)) {
    return { element: snapshot(element), previousRotationRad, unchanged: true };
  }
  selectExactly([element.id]);
  withAgentTurn(turnId, () => useEditorStore.getState().updateSelectedTransform(
    element.transform.translationMm,
    radians,
  ));
  return { element: snapshot(requireElement(element.id)), previousRotationRad, unchanged: false };
}

function deleteElements(input: RecordInput, turnId: string): unknown {
  const ids = input.elementIds as string[];
  const scene = useEditorStore.getState().scene;
  const elements = ids.map((id) => requireElement(id, scene));
  const names = elements.map((element) => element.name);
  selectExactly(ids);
  withAgentTurn(turnId, () => useEditorStore.getState().deleteSelection());
  return { deletedIds: ids, deletedNames: names };
}

async function listCatalogFolders(input: RecordInput): Promise<unknown> {
  const response = await api<{ data: Array<Record<string, unknown>> }>("/catalog-folders?catalogSchema=engineering-v2");
  const folders = input.kind
    ? response.data.filter((folder) => folder.kind === input.kind)
    : response.data;
  return { folders };
}

function selectExactly(ids: string[]): void {
  const store = useEditorStore.getState();
  store.select(null);
  ids.forEach((id, index) => useEditorStore.getState().select(id, index > 0));
}

function findElement(id: string, scene = useEditorStore.getState().scene): SceneElement {
  const element = scene.elements.find((candidate) => candidate.id === id);
  if (!element) throw new Error(`Elemento ${id} não foi encontrado na cena aberta.`);
  return element;
}

function requireElement(id: string, scene = useEditorStore.getState().scene): SceneElement {
  const element = findElement(id, scene);
  if (element.locked) throw new Error(`“${element.name}” está bloqueado e não pode ser alterado.`);
  return element;
}

function requireSceneTarget(id: string, scene = useEditorStore.getState().scene): void {
  if (!findSceneTargetName(scene, id)) throw new Error(`Item ${id} não foi encontrado na cena aberta.`);
}

function findSceneTargetName(scene: SceneDocument, id: string): string | null {
  return scene.elements.find((candidate) => candidate.id === id)?.name
    ?? (scene.walls.some((candidate) => candidate.id === id) ? `Parede ${id}` : null)
    ?? scene.rooms.find((candidate) => candidate.id === id)?.name
    ?? (scene.openings.some((candidate) => candidate.id === id) ? `Vão ${id}` : null);
}

function snapshot(element: SceneElement): Record<string, unknown> {
  const componentry = elementComponentry(element);
  const renderedState = renderedElementEnvelopeState(element);
  const renderedBounds = renderedState.envelope;
  const renderedSize = renderedElementSizeMm(element);
  return {
    id: element.id,
    name: element.name,
    kind: element.kind,
    translationMm: element.transform.translationMm,
    rootTranslationMm: element.transform.translationMm,
    physicalCenterMm: renderedElementCenterMm(element),
    rotationRad: element.transform.rotationRad,
    dimensionsMm: element.dimensionsMm,
    renderedDimensionsMm: {
      width: renderedSize[0],
      height: renderedSize[1],
      depth: renderedSize[2],
    },
    renderedBoundsMm: {
      minimum: renderedBounds.minimumMm,
      maximum: renderedBounds.maximumMm,
    },
    renderedLocalBoundsMm: {
      minimum: renderedBounds.minimumMm,
      maximum: renderedBounds.maximumMm,
    },
    renderedGeometry: renderedGeometryEvidence(element),
    materialId: element.materialId,
    materialAssignments: element.materialAssignments,
    catalogRef: element.catalogRef,
    // O que o módulo tem (portas, gavetas…), lido da árvore de componentes: o
    // pedido descreve móveis pelo conteúdo e o nome raramente o diz.
    ...(componentry ? { componentry } : {}),
    locked: element.locked,
    visible: element.visible,
  };
}

function renderedGeometryEvidence(element: SceneElement): Record<string, unknown> {
  const state = renderedElementEnvelopeState(element);
  if (!dynamicAssetIdFor(element)) {
    return {
      status: "ready",
      source: "parametric_exact",
      pose: "closed",
      elementRevision: element.revision,
      measuredRevision: element.revision,
    };
  }
  return {
    status: state.status,
    source: state.source,
    pose: "closed",
    elementRevision: state.elementRevision,
    measuredRevision: state.measuredRevision,
  };
}

function refreshElementSnapshots(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const record = value as Record<string, unknown>;
  const refreshed = { ...record };
  const element = record.element as Record<string, unknown> | undefined;
  if (typeof element?.id === "string") {
    const current = useEditorStore.getState().scene.elements.find((candidate) => candidate.id === element.id);
    if (current) refreshed.element = snapshot(current);
  }
  if (Array.isArray(record.elements)) {
    refreshed.elements = record.elements.map((entry) => {
      const candidate = entry as Record<string, unknown> | undefined;
      if (typeof candidate?.id !== "string") return entry;
      const current = useEditorStore.getState().scene.elements.find((item) => item.id === candidate.id);
      return current ? snapshot(current) : entry;
    });
  }
  return refreshed;
}

const GEOMETRY_DEPENDENT_CAPABILITIES = new Set([
  "scene.element.read",
  "scene.element.move",
  "scene.element.nudge",
  "scene.element.mount",
  "scene.element.rotate",
  "scene.element.resize",
  "scene.element.place",
  "scene.placement.evaluate",
  "scene.placement.resolve",
  "scene.check.clearance",
  "scene.check.grounded",
  "scene.check.installation",
  "scene.part.interact",
  "scene.part.setState",
]);

const GEOMETRY_MUTATION_CAPABILITIES = new Set([
  "scene.module.add",
  "scene.module.install",
  "scene.element.move",
  "scene.element.nudge",
  "scene.element.mount",
  "scene.element.rotate",
  "scene.element.resize",
  "scene.element.place",
  "scene.element.duplicate",
  "scene.element.appearance.match",
  "scene.part.interact",
  "scene.part.setState",
  "scene.part.attribute.set",
]);

function normalizeLookupText(value: string): string {
  return value.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

function targetSnapshot(scene: SceneDocument, id: string): Record<string, unknown> {
  const element = scene.elements.find((candidate) => candidate.id === id);
  if (element) return snapshot(element);
  const wall = scene.walls.find((candidate) => candidate.id === id);
  if (wall) return { id, name: `Parede ${id}`, kind: "wall", materialId: wall.materialId, materialAssignments: wall.materialAssignments };
  const room = scene.rooms.find((candidate) => candidate.id === id);
  if (room) return { id, name: room.name, kind: "room", materialAssignments: room.materialAssignments };
  const opening = scene.openings.find((candidate) => candidate.id === id);
  if (opening) return { id, name: `Vão ${id}`, kind: opening.kind, materialId: opening.materialId, materialAssignments: opening.materialAssignments };
  throw new Error(`Item ${id} não foi encontrado depois da pintura.`);
}

function createdElementIdsForTurn(turnId: string): Set<string> {
  const created = new Set<string>();
  const command = `agent:${turnId}`;
  for (const entry of useEditorStore.getState().history) {
    if (entry.command !== command) continue;
    const beforeIds = new Set(entry.before.elements.map((element) => element.id));
    for (const element of entry.after.elements) {
      if (!beforeIds.has(element.id)) created.add(element.id);
    }
  }
  return created;
}

async function fetchDynamicGraph(assetId: string): Promise<DynamicComponentGraph> {
  const response = await fetch(apiAssetUrl(assetId), { credentials: "include" });
  if (!response.ok) throw new Error(`Falha ${response.status} ao ler o componente dinâmico.`);
  return (await response.json()) as DynamicComponentGraph;
}

function readScene(scene: SceneDocument, detail: string): Record<string, unknown> {
  const base: Record<string, unknown> = {
    documentId: scene.documentId,
    name: scene.metadata.name,
    units: scene.units,
    coordinateSystem: scene.coordinateSystem,
    counts: {
      walls: scene.walls.length,
      openings: scene.openings.length,
      rooms: scene.rooms.length,
      elements: scene.elements.length,
      materials: scene.materials.length,
    },
    selectedIds: useEditorStore.getState().selectedIds,
  };
  if (detail === "summary") {
    base.elements = scene.elements.map((element) => ({
      id: element.id,
      name: element.name,
      kind: element.kind,
      dimensionsMm: element.dimensionsMm,
      renderedDimensionsMm: (() => {
        const size = renderedElementSizeMm(element);
        return { width: size[0], height: size[1], depth: size[2] };
      })(),
      translationMm: element.transform.translationMm,
    }));
    base.rooms = scene.rooms.map((room) => ({ id: room.id, name: room.name, areaMm2: room.areaMm2 }));
    return base;
  }
  if (["architecture", "all"].includes(detail)) {
    base.levels = scene.levels;
    base.walls = scene.walls.map((wall) => ({
      id: wall.id,
      startMm: wall.startMm,
      endMm: wall.endMm,
      heightMm: wall.heightMm,
      thicknessMm: wall.thicknessMm,
      openingIds: wall.openingIds,
    }));
    base.openings = scene.openings;
    base.rooms = scene.rooms;
  }
  if (["elements", "all"].includes(detail)) {
    base.elements = scene.elements.map(snapshot);
    base.materials = scene.materials.map((material) => ({ id: material.id, name: material.name, category: material.category }));
  }
  return base;
}

/**
 * Typology classification improves with dimensions and catalogue names, so
 * hand the reviewer everything the scene knows about the target and the
 * neighbours it measured against.
 */
export function installationReviewDescriptors(
  scene: SceneDocument,
  element: SceneElement,
  neighborIds: ReadonlyArray<string>,
): InstallationReviewDescriptors {
  const describe = (candidate: SceneElement): TypologyDescriptor => ({
    name: candidate.name,
    category: candidate.catalogRef?.itemId ?? null,
    dimensionsMm: candidate.dimensionsMm,
  });
  const neighbors: Record<string, TypologyDescriptor> = {};
  for (const id of neighborIds) {
    const neighbor = scene.elements.find((candidate) => candidate.id === id);
    if (neighbor) neighbors[neighbor.name] = describe(neighbor);
  }
  return { element: describe(element), neighbors };
}

function mutationSupportsDesignAttribute(capability: string, attribute: string): boolean {
  if (["scene.module.add", "scene.module.install", "scene.element.duplicate"].includes(capability)) {
    return ["product", "quantity", "position", "orientation", "dimensions", "relation", "material", "appearance"].includes(attribute);
  }
  if (["scene.element.move", "scene.element.nudge", "scene.element.place", "scene.element.mount"].includes(capability)) {
    return ["position", "orientation", "relation"].includes(attribute);
  }
  if (capability === "scene.element.rotate") return attribute === "orientation";
  if (capability === "scene.element.resize") return attribute === "dimensions";
  if (capability === "scene.element.paint") return attribute === "material" || attribute === "appearance";
  if (capability === "scene.element.appearance.match") return attribute === "appearance" || attribute === "material";
  if (["scene.part.interact", "scene.part.setState", "scene.part.attribute.set"].includes(capability)) {
    return attribute === "function";
  }
  return false;
}

function planSubjectsMatch(left: string, right: string): boolean {
  return subjectNamesAgree(left, right);
}

export function nextVisualRecoveryView(
  targetIds: string[],
  priorViews: InspectionCameraMetadata[],
): { azimuthDeg: number; elevationDeg: number; distanceScale: number } | null {
  const relevant = priorViews.filter((camera) => camera.targetMetrics.some((metric) => targetIds.includes(metric.elementId)));
  if (!relevant.length) return null;
  const qualified = relevant
    .filter((camera) => camera.targetMetrics.some((metric) => (
      targetIds.includes(metric.elementId)
      && metric.visibilityRatio >= 0.7
      && metric.frameCoverageRatio >= 0.05
      && !metric.clipped
    )))
    .sort((left, right) => right.score - left.score);
  const anchor = qualified[0] ?? relevant[0];
  if (!anchor) return null;
  const offsets = [38, -38, 72, -72, 108, -108, 146, -146, 180];
  const azimuthDeg = offsets
    .map((offset) => normalizeVisualDegrees(anchor.azimuthDeg + offset))
    .find((candidate) => relevant.every((view) => visualAngularDistance(candidate, view.azimuthDeg) >= 20));
  if (azimuthDeg === undefined) return null;
  const elevations = [22, 30, 15, 36];
  return {
    azimuthDeg,
    elevationDeg: elevations[Math.min(relevant.length - 1, elevations.length - 1)]!,
    // A slightly wider frame prevents an oblique recovery view from being
    // rejected merely because a corner crossed the viewport boundary.
    distanceScale: Math.min(1.55, 1.14 + Math.min(relevant.length, 6) * 0.05),
  };
}

export function visualRecoveryFitIds(
  framingIds: string[],
  reviewTargetIds: string[],
  recovering: boolean,
): string[] {
  return recovering && reviewTargetIds.length ? reviewTargetIds : framingIds;
}

export function accumulatedVisualCoverage(
  targetIds: string[],
  views: InspectionCameraMetadata[],
  current: ViewportCaptureResult["visualReview"],
  accumulatedCaptureCount: number,
): ViewportCaptureResult["visualReview"] {
  const minimumVisibilityRatio = 0.7;
  const minimumFrameCoverageRatio = 0.05;
  const minimumContextFrameCoverageRatio = 0.012;
  // A custom recovery capture can legitimately contain a single strong view,
  // but it must not bypass the two-angle requirement by reporting itself as
  // covered in isolation. Trust direct coverage only when the current capture
  // itself proves two independent qualified views.
  const directlyCovered = new Set(current.targets
    .filter((target) => target.status === "covered" && target.qualifiedViewCount >= 2)
    .map((target) => target.elementId));
  const targets = targetIds.map((elementId) => {
    const metrics = views.flatMap((camera) => camera.targetMetrics
      .filter((metric) => metric.elementId === elementId)
      .map((metric) => ({ camera, metric })));
    const qualified = metrics.filter(({ metric }) => (
      metric.visibilityRatio >= minimumVisibilityRatio
      && metric.frameCoverageRatio >= minimumFrameCoverageRatio
      && !metric.clipped
    ));
    const contextual = metrics.filter(({ metric }) => (
      metric.visibilityRatio >= minimumVisibilityRatio
      && metric.frameCoverageRatio >= minimumContextFrameCoverageRatio
      && !metric.clipped
    ));
    const diverseQualified: typeof qualified = [];
    for (const entry of qualified) {
      if (!diverseQualified.length || diverseQualified.every((previous) => (
        visualAngularDistance(previous.camera.azimuthDeg, entry.camera.azimuthDeg) >= 24
        || Math.abs(previous.camera.elevationDeg - entry.camera.elevationDeg) >= 18
        || previous.camera.sectioned !== entry.camera.sectioned
      ))) diverseQualified.push(entry);
    }
    // A professional composition review needs one detailed image and one
    // independent contextual image. Requiring the target to fill >=5% in both
    // made a small module beside a wardrobe impossible to approve: the wider
    // view that proves their relationship is intentionally less zoomed-in.
    const detailedAnchor = diverseQualified[0];
    const contextualCompanion = detailedAnchor && contextual.find((entry) => entry !== detailedAnchor && (
      visualAngularDistance(detailedAnchor.camera.azimuthDeg, entry.camera.azimuthDeg) >= 24
      || Math.abs(detailedAnchor.camera.elevationDeg - entry.camera.elevationDeg) >= 18
      || detailedAnchor.camera.sectioned !== entry.camera.sectioned
    ));
    const evidenceViewCount = diverseQualified.length >= 2 || contextualCompanion
      ? Math.max(2, diverseQualified.length)
      : diverseQualified.length;
    const covered = directlyCovered.has(elementId) || evidenceViewCount >= 2;
    return {
      elementId,
      bestVisibilityRatio: Math.max(0, ...metrics.map(({ metric }) => metric.visibilityRatio)),
      bestFrameCoverageRatio: Math.max(0, ...metrics.map(({ metric }) => metric.frameCoverageRatio)),
      qualifiedViewCount: evidenceViewCount,
      status: covered ? "covered" as const : "insufficient" as const,
    };
  });
  const coveredIds = targets.filter((target) => target.status === "covered").map((target) => target.elementId);
  const issues = targets
    .filter((target) => target.status === "insufficient")
    .map((target) => `${target.elementId}: faltam ângulos legíveis e independentes para a aprovação visual.`);
  return {
    status: issues.length ? "insufficient" : "ready",
    candidatesEvaluated: current.candidatesEvaluated,
    selectedImages: current.selectedImages,
    coveredIds,
    targets,
    issues,
    accumulatedCaptureCount,
  };
}

function visualAngularDistance(left: number, right: number): number {
  const difference = Math.abs(((left - right + 540) % 360) - 180);
  return Math.min(180, difference);
}

function normalizeVisualDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function summarizeSceneForPrompt(scene: SceneDocument): string {
  const selectedIds = useEditorStore.getState().selectedIds;
  const names = scene.elements.slice(0, 40).map((element) => `${element.name} (${element.id})`);
  const overflow = Math.max(0, scene.elements.length - names.length);
  return [
    `${scene.rooms.length} ambiente(s), ${scene.walls.length} parede(s), ${scene.openings.length} vão(s) e ${scene.elements.length} elemento(s).`,
    names.length ? `Elementos: ${names.join(", ")}${overflow ? ` e mais ${overflow}` : ""}.` : "Cena sem módulos.",
    selectedIds.length ? `Seleção atual: ${selectedIds.join(", ")}.` : "Nada selecionado.",
  ].join(" ");
}
